import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog"
import { Button } from "../../Components/UI/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../Components/UI/tooltip"
import { Card, CardContent } from "../../Components/UI/card"
import { X, Plus, User, Building2, Clock, Check, Pencil, ArrowLeft, ListTodo, History, ClipboardList, Download, Star, FileText, Paperclip, Edit2, Bell, BellRing, MapPin, Phone, Users, Mail, PhoneOutgoing, PhoneMissed, MessageSquare, Filter, ShoppingBag, TrendingUp, Package, Calendar, MessageSquareText } from "lucide-react"
import { FaSackDollar } from "react-icons/fa6"
import { GiIceCube } from "react-icons/gi";
import { useCurrency } from "../../Hooks/useCurrency";
import { MdPhone } from "react-icons/md";
import { BsBellFill, BsEnvelopeFill, BsWhatsapp, BsHourglassBottom, BsHourglassTop } from "react-icons/bs";
import Upcoming from "../../Components/Common/Upcoming";
import ThreadDropdown from "../Threads/AddThreadDropdown"
import TaskDetailDialog from "../Tasks/TaskDetails"
import ThreadEntry from "../Threads/ThreadEntry"
import ConnectionEntryDialog from "./ConnectionEntry"
import TaskEntry from "../Tasks/TaskEntry"
import RecordEntry from "../Records/RecordsEntry"
import { TaskCompleteDialog } from "../../Components/Common/TaskCompleteDialog"
import { performThreadActionAPI, updateThreadStageAPI, reassignThreadAPI, manageThreadAPI } from '../Threads/ThreadsAPI';
import { getUsersArr, getThreadTypesArr, getAgencyDetails, getContractsArr, checkIfInqOnlyMode } from "../../Store/Reducers"
import {
    getRelativeDateText, getTaskColor, getColorClass, createEmailLink, getDateWithoutTime, getCurrencySymbol, getTransactionIconColorClass,
    getTransactionIcon, getTransactionTypeById, timeAgo, getThreadNameFromTypeID, currentUserHasPermission, getCurrentUser, getFdbkScoreText, getThreadModeTitle, getThreadTypeMeta,
    isPendingTasks, formatRecordText,
    formatAmount,
    canEditTask,
    getMyAgenciesMappedIDs,
    getUploadUrl
} from "../../Utils/Utilities"
import { getConnectionDetailAPI, manageFdbkRequestAPI, manageConnectionAPI, manageConnectionAccessAPI } from "./ConnectionAPI"
import { getFromIndexedDB } from "../../Utils/IndexedDBUtils"
import RecordHistoryDialog from "./Tabs/RecordHistory"
import { format, isSameDay } from 'date-fns';
import ContactActions from "../../Components/Common/ContactActions"
import { handlePhoneClickAction, handleWhatsAppClickAction } from "../../Utils/ContactLogUtils"
import { completeTaskAPI, cancelTaskAPI, rejectTaskAPI, remindTaskAPI } from '../Tasks/TasksAPI';
import { toast } from "sonner";
import TaskHistoryDialog from "./Tabs/TaskHistory";
import { useTax } from "../../Hooks/useTax"
import TransactionEntry from "./Tabs/TransactionEntry"
import TransactionDropdown from './TransactionDropdown';
import ContractEntry from "../Contracts/ContractEntry"
import ContractDropdown from './ContractDropdown';
import OtherContactsEntry from "./OtherContactsEntry"
import { AlertBox } from "../../Components/Common/AlertBox"
import { sortByDate } from "../../Utils/Utilities"
import { useConnectionByIDAnyAgency, useThreadsByConnID, useActiveThreadsByConnID, useTasksByConnID, useContactsByConnID } from "../../Hooks/useConnectionData"
import { useNavigate } from "react-router-dom"
import { getTableDateOrWeekday } from "../../Utils/DateTime"
import ThreadSahiIcon from "../../Components/Common/ThreadSahiIcon"
import useIsMobile from "../../Hooks/useIsMobile"
import CompleteThreadDialog from "../../Components/Common/CompleteThreadDialog"
import CancelThreadDialog from "../../Components/Common/CancelThreadDialog"
import AttachmentPreviewModal from "../../Components/Common/AttachmentPreviewModal"
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
} from "../../Components/UI/alert-dialog"
import ReassignDialog from "../Threads/ReassignDialog"
import { getPriorityDetails } from '../../Utils/Constants';
import { sentimentEmoji } from "../../Utils/Utilities"
import { getThreadTypeIcon } from "../../Utils/Utilities"
import ContractDetailDialog from "./Tabs/ContractDetails"
import { setGlobalLoading } from "../../Store/Actions"
import CustomTooltip from "../../Components/Common/CustomTooltip"
import CustomSearchableDropdown from "../../Components/Common/CustomSearchableDD"
import Policies from "../Settings/Policies/Policies"
import { getActiveThreadTypesArr } from "../../Store/Reducers"
import WspDialog from "../../Components/Common/WspDialog"
import NoDataCard from "../../Components/Common/NoDataCard"
import { getAgencyData, getStorageData } from "../../Utils/Storage"
import ThrDetails from "../Threads/ThrDetails"
import ThrDetailsInfo from "../Threads/ThrDetails/ThrDetailsInfo"
import ThrDetailsStatus from "../Threads/ThrDetails/ThrDetailsStatus"
import ThrDetailsActions from "../Threads/ThrDetails/ThrDetailsActions"
import { getAuthData } from "../../Utils/AuthUtils"
import apiService from "../../Services/APIService"
import ConditionalDiv from "../../Components/Common/ConditionalDiv"

// Reusable styling classes for consistency and maintainability
const styleClasses = {
    // Layout classes
    sectionCard: "bg-sahi-white !shadow-sm !border-none !rounded-xl",
    sectionHeader: "flex items-center justify-between mb-3 p-3 pb-0",
    sectionTitle: "text-lg font-semibold",
    itemCard: "bg-sahi-disable p-3 rounded-lg",
    emptyState: "bg-sahi-disable p-8 rounded-lg text-center border border-sahi-border-color",

    // Typography classes
    textColor: "text-color",
    textSubColor: "text-sub-text-color",
    titleText: "text-sm font-semibold text-color truncate",
    bodyText: "text-xs text-sub-text-color",

    // Button classes
    primaryButton: "h-8 px-3",
    secondaryButton: "h-8 w-8 p-0",
    actionButton: "h-6 w-6 p-0",
    iconButton: "h-7 w-7 p-0 hover:bg-sahi-green-light rounded-md",
    commonBtn: "text-sm px-3 py-1.5 h-auto",

    // Color classes
    greenAction: "text-sahi-green hover:bg-sahi-green-light hover:text-sahi-green",
    redAction: "text-sahi-red hover:bg-red-50 hover:text-sahi-red",
    blueAction: "text-sahi-blue hover:bg-blue-50 hover:text-sahi-blue",

    // Layout utility classes
    flexBetween: "flex items-center justify-between",
    flexStart: "flex items-start justify-between",
    flexEnd: "flex items-end justify-between",
    flexCenter: "flex items-center justify-center",
    truncate: "truncate",
    spaceBetween: "flex justify-between items-center",

    // State classes
    emptyStateIcon: "h-12 w-12 mx-auto mb-3 text-sahi-grey",
    emptyStateText: "text-sm text-sub-text-color",
    loadingSpinner: "h-3 w-3 animate-spin border-2 border-t-transparent rounded-full",

    // Mobile specific classes
    mobileCard: "bg-zinc-50 rounded-lg border border-zinc-200 shadow-xs w-full flex flex-col justify-between overflow-hidden hover:border-zinc-300 transition-all",
    mobileHeader: "flex flex-col gap-1 p-3",
    mobileFooter: "flex justify-between items-center text-xs px-3 py-2 border-t border-zinc-200 w-full bg-zinc-50/70",
    cardSpacing: "mx-3 mt-3",
    headerSpacing: "px-4 pt-4 mt-2 bg-white"
}

// Reusable component for action buttons with loading states
const ActionButton = ({
    variant,
    icon: Icon,
    onClick,
    loading,
    tooltip,
    disabled = false,
    className = ""
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                variant="ghost"
                size="sm"
                className={`h-7 w-7 p-0 rounded-md transition-colors ${variant === 'complete' ? 'text-sahi-green hover:bg-sahi-green-light hover:text-sahi-green' :
                    variant === 'cancel' ? 'text-sahi-red hover:bg-red-50 hover:text-sahi-red' :
                        'text-sahi-blue hover:bg-blue-50 hover:text-sahi-blue'
                    } ${className}`}
                onClick={onClick}
                disabled={disabled || loading}
            >
                {loading ? (
                    <div className="h-3.5 w-3.5 animate-spin border-2 border-current border-t-transparent rounded-full" />
                ) : (
                    <Icon className="h-3.5 w-3.5" />
                )}
            </Button>
        </TooltipTrigger>
        <TooltipContent><p>{tooltip}</p></TooltipContent>
    </Tooltip>
)

// Reusable empty state component
const EmptyState = ({ icon: Icon, title, subtitle, onClick, clickable = true }) => (
    <div
        className={`${styleClasses.emptyState} ${clickable ? 'cursor-pointer hover:bg-zinc-50 transition-colors' : ''} ${!clickable ? 'mt-3' : ''}`}
        onClick={clickable ? onClick : undefined}
    >
        <Icon className={styleClasses.emptyStateIcon} />
        <p className="text-sm font-medium mb-1">{title}</p>
        <p className={styleClasses.emptyStateText}>{subtitle}</p>
    </div>
)

// Reusable section header component
const SectionHeader = ({ title, icon = null, onAdd, onHistory, onEdit, historyButton = false, customButtons = null, className = "" }) => (
    <div className={`${styleClasses.sectionHeader} ${className}`}>
        <div className="flex items-center gap-2">
            {icon}
            <h3 className={styleClasses.sectionTitle}>{title}</h3>
        </div>
        <div className="flex gap-3 items-center">
            {customButtons}
            {onAdd && (
                <div onClick={onAdd} className="cursor-pointer">
                    <Plus className="h-5 w-5" />
                </div>
            )}
            {onEdit && (
                <div
                    onClick={onEdit}
                    className="cursor-pointer"
                >
                    <Pencil className="h-4 w-4" />
                </div>
            )}
            {historyButton && onHistory && (
                <div onClick={onHistory} className="cursor-pointer">
                    <History className="h-4 w-4" />
                </div>
            )}
        </div>
    </div>
)

// Reusable badge component for status display
const StatusBadge = ({ children, className = "", onClick }) => (
    <div
        className={`bg-zinc-100 p-2 rounded text-xs ${className}`}
        onClick={onClick}
    >
        {children}
    </div>
)

// Reusable contact info component
const ContactInfo = ({ contact, isPrimary, onClick }) => (
    <div
        className={`${styleClasses.itemCard} cursor-pointer hover:bg-zinc-100 transition-colors`}
        onClick={onClick}
    >
        <div className={styleClasses.flexBetween}>
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{contact.CNm || "Unknown Contact"}</span>
                {isPrimary && (
                    <Star
                        className="h-3.5 w-3.5 fill-sahi-orange text-sahi-orange"
                    />
                )}
            </div>
            {contact.Dsgn && (
                <span className="text-xs text-sub-text-color">{contact.Dsgn}</span>
            )}
        </div>
    </div>
)

const ConnectionDetailsMobile = ({ isOpen, onClose, connID, activeTab = "Overview", expandThreadID, isMobileContactActionsOpen, onCloseContactActions }) => {
    const dispatch = useDispatch();
    const currencySymbol = useCurrency();
    const taxIDLabel = useTax();
    const users = useSelector(getUsersArr);
    const threadTypesData = useSelector(getThreadTypesArr);
    const agencyData = useSelector(getAgencyDetails);
    const navigate = useNavigate();
    const [attachDocDialog, setAttachDocDialog] = useState(false);
    const [completedThreadForAttachment, setCompletedThreadForAttachment] = useState(null);
    const attachFileInputRef = useRef(null);
    const [previewFilePath, setPreviewFilePath] = useState(null);
    const [isFullPreviewOpen, setIsFullPreviewOpen] = useState(false);
    const [isFullPreviewLoading, setIsFullPreviewLoading] = useState(true);
    const [fullPreviewUrl, setFullPreviewUrl] = useState("");
    const [businessCardRatio, setBusinessCardRatio] = useState(null);
    const { connection, isOwnAgencyConnection } = useConnectionByIDAnyAgency(connID);
    const businessCardPath = connection?.ContactCard || connection?.Attachment || connection?.attachment;
    const connTypeStr = (connection?.ConnType || "").toString().toUpperCase();
    const isSupplierOrOther = connTypeStr.includes("S") || connTypeStr.includes("O") || connTypeStr.includes("SUPPLIER") || connTypeStr.includes("OTHER");

    useEffect(() => {
        setBusinessCardRatio(null);
    }, [businessCardPath]);
    const isMobile = useIsMobile()
    const hasAgencyAccess = useMemo(() => {
        if (!connection?.AgencyID) return false;
        const myAgenciesData = getStorageData('MyAgencies') || [];
        return myAgenciesData.some(agn => String(agn.AgencyID) === String(connection.AgencyID));
    }, [connection?.AgencyID]);
    const resolvedUsers = useMemo(() => {
        if (!connection?.AgencyID) return users;
        try {
            const allUsers = getStorageData('U') || [];
            const agencyUsers = allUsers.filter(u => String(u.AgencyID) === String(connection.AgencyID));
            if (agencyUsers.length > 0) {
                return agencyUsers.map(u => ({
                    userID: u.UserID,
                    name: u.Name,
                    isAdmin: u.IsAdmin,
                    fullAccess: u.FullAccess,
                    connViewTypeID: u.ConnViewTypeID
                }));
            }
        } catch (e) {
            console.error('Error resolving agency users:', e);
        }
        return users;
    }, [users, connection?.AgencyID]);
    const canEditThread = currentUserHasPermission('ThrActions');
    const xnActions = currentUserHasPermission('XnActions');
    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
    const showModule = { transactions: agencyData?.EnblXns, contracts: agencyData?.EnblContracts };

    const perm = currentUserHasPermission('CtrcActions');
    const canEditContracts = isOwnAgencyConnection && (perm === 1 || perm === 2);
    const canAddCtrc = isOwnAgencyConnection && (perm != 0 || perm <= 3)
    const activeThreadTypesArr = useSelector(getActiveThreadTypesArr);
    const currActiveThreadType = getThreadModeTitle(activeThreadTypesArr);

    const canEditXn = isOwnAgencyConnection && (xnActions === 1 || xnActions === 2);
    const canShowEditButton = isOwnAgencyConnection && canEditThread <= 2 && canEditThread > 0;
    const [contractsFromAPI, setContractsFromAPI] = useState([]);
    const canEditConnections = isOwnAgencyConnection ? currentUserHasPermission('ConnEdit') : 0;
    const canDeleteConnections = isOwnAgencyConnection ? currentUserHasPermission('ConnDel') : 0;
    const canCreateConnections = isOwnAgencyConnection ? currentUserHasPermission('ConnEntry') : 0;

    const agencyDB = getAgencyData();
    const { data: activeData } = useSelector((state) => state.activeData) || {};
    const authData = getAuthData();
    const currentUserID = authData?.UserID;

    // State management
    const [editingThreadID, setEditingThreadID] = useState(null);
    const [threadsFromDB, setThreadsFromDB] = useState([]);

    useEffect(() => {
        if (!connID || isOwnAgencyConnection) {
            setThreadsFromDB([]);
            return;
        }

        const loadThreads = async () => {
            try {
                const dbThreads = await getFromIndexedDB('Threads') || [];
                const openIDs = connection?.OpenThreadIDs ? connection.OpenThreadIDs.split(',').map(id => id.trim()) : [];
                const related = dbThreads.filter(t =>
                    String(t.ConnID || t.connID) === String(connID) ||
                    openIDs.includes(String(t.ThreadID || t.threadID))
                );
                setThreadsFromDB(related);
            } catch (e) {
                console.error('Error loading threads from IndexedDB fallback:', e);
            }
        };

        loadThreads();
    }, [connID, connection?.OpenThreadIDs, isOwnAgencyConnection]);
    const hasExpandedRef = useRef(false);

    useEffect(() => {
        if (!isOpen) hasExpandedRef.current = false;
    }, [isOpen]);
    const [editingThreadTypeID, setEditingThreadTypeID] = useState(null);
    const [editingTaskID, setEditingTaskID] = useState(null);
    const [selectedThread, setSelectedThread] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedTaskForRecord, setSelectedTaskForRecord] = useState(null);
    const [taskEntryMode, setTaskEntryMode] = useState('normal'); // 'normal' | 'repeat'
    const [repeatPrefillData, setRepeatPrefillData] = useState(null);
    const pendingRepeatAfterRecordRef = useRef(false);
    const [selectedThreadForAction, setSelectedThreadForAction] = useState(null);
    const [selectedContractForDetail, setSelectedContractForDetail] = useState(null);
    const [selectedContactForDetail, setSelectedContactForDetail] = useState(null);
    const [isWspDialogOpen, setIsWspDialogOpen] = useState(false);

    // Transaction states
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionPrefilledData, setTransactionPrefilledData] = useState({});
    // Contract states
    const [selectedTransactionType, setSelectedTransactionType] = useState(1);
    const [transactionDropdownOpen, setTransactionDropdownOpen] = useState(false);

    // Contract states
    const [selectedContract, setSelectedContract] = useState(null);
    const [contractPrefilledData, setContractPrefilledData] = useState({});
    const [selectedContractType, setSelectedContractType] = useState('sold');
    const [contractDropdownOpen, setContractDropdownOpen] = useState(false);

    // Loading states
    const [taskActionLoading, setTaskActionLoading] = useState({});
    const [threadActionLoading, setThreadActionLoading] = useState({});
    const [loadingRecords, setLoadingRecords] = useState(false);

    // Data from hooks
    const contractsFromRedux = useSelector(getContractsArr);
    const rawAllThreads = useThreadsByConnID(connID);
    const allThreads = useMemo(() => {
        return isOwnAgencyConnection ? rawAllThreads : threadsFromDB;
    }, [rawAllThreads, threadsFromDB, isOwnAgencyConnection]);

    const rawActiveThreads = useActiveThreadsByConnID(connID);
    const activeThreads = useMemo(() => {
        if (isOwnAgencyConnection) return rawActiveThreads;
        return threadsFromDB.filter(t => Number(t.StatusID || t.statusID || t.Sts || t.sts || 0) !== 2 && Number(t.StatusID || t.statusID || t.Sts || t.sts || 0) !== 3);
    }, [rawActiveThreads, threadsFromDB, isOwnAgencyConnection]);
    const tasksFromConnRaw = useTasksByConnID(connID);
    const tasksFromConn = useMemo(() => {
        // useTasksByConnID already parses and maps tasks from connection.Tasks merged with Redux updates.
        return tasksFromConnRaw.filter(task => Number(task.Sts || task.status || 0) === 1);
    }, [tasksFromConnRaw]);
    const currentUser = getCurrentUser();

    const tasks = useMemo(() => {
        if (currentUser?.IsAdmin) return tasksFromConn;

        // Filter tasks: User must be either the Creator or the assigned Executive (POC)
        return tasksFromConn.filter(task =>
            Number(task.UID) === Number(currentUserID) ||
            Number(task.CreatedUID) === Number(currentUserID)
        );
    });
    const contacts = useContactsByConnID(connID);

    // Add refs to your sections
    const threadsRef = useRef(null);
    const ordersRef = useRef(null);
    const tasksRef = useRef(null);
    const transactionsRef = useRef(null);
    const contractsRef = useRef(null);
    const contactsRef = useRef(null);
    const mainContentScrollRef = useRef(null);

    // Function to scroll to a specific section
    const scrollToSection = (ref) => {
        ref.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    // Dialog states
    const [dialogStates, setDialogStates] = useState({
        threadEntry: false,
        taskEntry: false,
        transactionEntry: false,
        contractEntry: false,
        contractDetail: false,
        contactDetail: false,
        recordEntry: false,
        editDialog: false,
        editContactsDialog: false,
        editSingleContactDialog: false,
        otherContacts: false,
        recordHistory: false,
        recordTypeFilter: null,
        taskHistory: false,
        upcoming: false,
        threadDetail: false,
        taskDetail: false,
        confirmCompleteDialog: false,
        confirmCancelDialog: false,
        confirmThreadCompleteDialog: false,
        confirmThreadCancelDialog: false,
        fdbkRequest: false,
        emailConfig: false,
        isInquiryOnlyMode: isInqOnlyMode,
        prefilledData: null,
        flag: false,
        orderEntry: false,
        plainOrderEntry: false,
        isHistoryview: false,
        ordHistoryEdit: false,
        thrDetails: false,
        convertToSales: false,
        reassignDialog: false,
        accessDialog: false,
        allUsersHaveAccessAlert: false,
    });

    const [accessUserIDs, setAccessUserIDs] = useState([]);
    const [removeAccessState, setRemoveAccessState] = useState({ open: false, userID: null });

    const [dataStates, setDataStates] = useState({
        selectedThreadType: 1,
        loadingRecords: false,
    });

    const [records, setRecords] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [feedbackThreadDetail, setFeedbackThreadDetail] = useState({});
    const [ordersHistory, setOrdersHistory] = useState([]);
    const [userPendingRIDs, setUserPendingRIDs] = useState([]); // Store user's assigned pending RIDs

    const openOrders = useMemo(() => {
        return activeThreads.filter(t => Number(t.TypeID || t.typeID || 0) === 2);
    }, [activeThreads]);

    const allOrders = useMemo(() => {
        const historyIDs = new Set(ordersHistory.map(o => String(o.ThreadID || o.threadID)));
        const uniqueOpen = openOrders.filter(o => !historyIDs.has(String(o.ThreadID || o.threadID)));
        return [...uniqueOpen, ...ordersHistory];
    }, [openOrders, ordersHistory]);

    const masterData = useSelector((state) => state.masterData)
    const regions = masterData?.regions;
    const threadStages = masterData?.threadStages;

    const stageMap = useMemo(() => {
        const dynamicMap = {};
        threadStages[0].Stages.forEach(stage => {
            dynamicMap[stage.SID] = {
                name: stage.S,
                bg: "bg-sahi-badge-bg-color",
            };
        });

        return dynamicMap;
    }, [threadStages]);

    const { ownEmail } = useSelector(state => state.masterData);
    const hasOwnEmail = ownEmail && ownEmail.length > 0 && ownEmail[0]?.length > 0;
    const lastFeedbackDT = connection?.LastFdbkReqDT ? new Date(connection?.LastFdbkReqDT) : null;
    const isFeedbackSentToday = lastFeedbackDT && isSameDay(lastFeedbackDT, new Date());

    // Parse rejection reasons for the current thread type
    const rejectionReasonOptions = useMemo(() => {
        if (!selectedThreadForAction || !masterData?.rejectionReasons) return []

        const currentThreadType = selectedThreadForAction.TypeID
        const threadReasons = masterData.rejectionReasons.find(item => item.TID === currentThreadType)

        if (!threadReasons) return []

        try {
            const reasons = JSON.parse(threadReasons.Rsns)
            return reasons.map(reason => ({
                value: reason.RID,
                label: reason.R,
            }))
        } catch (error) {
            console.error("Error parsing failure reasons:", error)
            return []
        }
    }, [selectedThreadForAction, masterData?.rejectionReasons])

    // Helper function to get executive name by user ID
    const getExecutiveName = (pocUserID, badge = false) => {
        const user = users.find((user) => user.userID === pocUserID);
        if (user) return user.name;

        try {
            const allUsers = getStorageData('U') || [];
            const fallbackUser = allUsers.find(u => Number(u.UserID) === Number(pocUserID));
            if (fallbackUser) return fallbackUser.Name;
        } catch (e) {
            console.error('Error finding executive name in fallback:', e);
        }

        return badge ? "Not assigned" : "--";
    };

    // Helper function to get threads and tasks data (excluding Sales Orders: TypeID 2)
    const getThreadsData = () => activeThreads.filter(t => Number(t.TypeID || t.typeID || 0) !== 2);

    // Helper function to determine connection types
    const getConnectionType = () => {
        let connType = [];
        const typeStr = connection?.ConnType || "";
        if (typeStr.includes("C")) {
            connType.push("Customer");
        }
        if (typeStr.includes("P")) {
            connType.push("Prospect");
        }
        if (typeStr.includes("S")) {
            connType.push("Supplier");
        }
        if (typeStr.includes("O")) {
            connType.push("Other");
        }
        return connType;
    }

    const getTasksData = () => {
        if (currentUser?.IsAdmin) return tasksFromConn;

        // Filter tasks: User must be either the Creator or the assigned Executive (POC)
        return tasksFromConn.filter(task =>
            Number(task.UID) === Number(currentUserID) ||
            Number(task.CreatedUID) === Number(currentUserID)
        );
    };

    // Fetch pending records from activeData and extract RIDs when component loads
    useEffect(() => {
        if (!isOpen || !activeData) {
            setUserPendingRIDs([]);
            return;
        }

        try {
            const pendingData = JSON.parse(activeData?.PendingRecords || '[]');

            // Find the current user's pending records
            const userPendingData = Array.isArray(pendingData)
                ? pendingData.find(item => item.UID === Number(currentUserID))
                : null;

            if (userPendingData && Array.isArray(userPendingData.PndgRecs)) {
                const allRIDs = [];

                // Extract RIDs from all pending records
                userPendingData.PndgRecs.forEach(record => {
                    if (record.RIDs) {
                        const rids = record.RIDs.split(',').map(r => r.trim());
                        allRIDs.push(...rids);
                    }
                });

                setUserPendingRIDs(allRIDs); // Store RIDs in state for filtering
            } else {
                setUserPendingRIDs([]);
            }
        } catch (error) {
            setUserPendingRIDs([]);
        }
    }, [isOpen, activeData, currentUserID]);

    useEffect(() => {
        if (expandThreadID && isOpen && !hasExpandedRef.current) {
            if (isMobileContactActionsOpen && contacts && contacts.length > 0) {
                const primaryContact = contacts[0];
                setSelectedContactForDetail(primaryContact);
                setDialogStates((prev) => ({ ...prev, contactDetail: true }));
                hasExpandedRef.current = true;
            } else {
                const openInquiries = getThreadsData();
                let foundThread = openInquiries?.find(t => t?.ThreadID === expandThreadID || t?.threadID === expandThreadID);

                // Check if there is only one active/open inquiry
                const isSingleActiveInquiry = foundThread && openInquiries?.length === 1;

                if (!foundThread) {
                    foundThread = activeThreads?.find(t => t?.ThreadID === expandThreadID || t?.threadID === expandThreadID);
                }

                if (!foundThread) {
                    foundThread = allThreads?.find(t => t?.ThreadID === expandThreadID || t?.threadID === expandThreadID);
                }

                if (!foundThread) {
                    foundThread = ordersHistory?.find(t => t?.ThreadID === expandThreadID || t?.threadID === expandThreadID);
                }

                if (foundThread) {
                    setSelectedThread(foundThread);
                    // Only open details if it's NOT a single active inquiry (since single inquiry details are already displayed inside the card)
                    if (!isSingleActiveInquiry) {
                        setDialogStates((prev) => ({ ...prev, thrDetails: true }));
                    }
                    hasExpandedRef.current = true;
                }
            }
        }
    }, [expandThreadID, isOpen, isMobileContactActionsOpen, contacts, activeThreads, allThreads, ordersHistory]);

    // Helper function to get next task date
    const getNextTaskDT = () => {
        let sortedTask = sortByDate(tasks, 'DT', 'asc');
        let nextTaskDT = sortedTask.find(task => task.Sts === 1)?.DT;
        return nextTaskDT;
    }

    // Helper function to get last interaction date
    const getLastInteractionDT = () => {
        let sortedRecord = sortByDate(records, 'DT', 'desc');
        let lastInteractionDT = sortedRecord[0]?.DT;
        return lastInteractionDT;
    }

    // Action handlers
    const handleCompleteTask = async (task) => {
        // Tasks embedded in connection.Tasks don't carry ConnID/BizName/ContactName of their own -
        // they're implicitly scoped to this connection, so stamp those on for the complete dialog's
        // "has a linked connection" check (Yes, Add Record / No, Just Complete).
        setSelectedTaskForRecord({
            ...task,
            ConnID: task.ConnID ?? connection?.ConnID,
            BizName: task.BizName ?? connection?.BizName,
            ContactName: task.ContactName ?? connection?.ContactName,
        });
        setDialogStates(prev => ({ ...prev, confirmCompleteDialog: true }));
    };

    const handleCancelTask = (task) => {
        setSelectedTaskForRecord(task);
        setDialogStates(prev => ({ ...prev, confirmCancelDialog: true }));
    };

    const handleCompleteThread = async (thread) => {
        setSelectedThreadForAction(thread);
        setDialogStates(prev => ({ ...prev, confirmThreadCompleteDialog: true }));
    };

    const handleCancelThread = (thread) => {
        setSelectedThreadForAction(thread);
        setDialogStates(prev => ({ ...prev, confirmThreadCancelDialog: true }));
    };

    const handleEditThread = (threadID, threadTypeID, threadData = null) => {
        setEditingThreadID(threadID);
        setEditingThreadTypeID(threadTypeID);
        setDataStates((prev) => ({ ...prev, selectedThreadType: threadTypeID }));
        setDialogStates(prev => ({ ...prev, threadEntry: true, prefilledData: threadData }));
    };

    const handleEditTask = (task) => {
        setEditingTaskID(task.TskID);
        setDialogStates(prev => ({ ...prev, taskEntry: true }));
    };

    const isReminderSentToday = (lastRemitterDate) => {
        if (!lastRemitterDate) return false;
        const lastReminderDate = new Date(lastRemitterDate);
        const today = new Date();
        return (
            lastReminderDate.getFullYear() === today.getFullYear() &&
            lastReminderDate.getMonth() === today.getMonth() &&
            lastReminderDate.getDate() === today.getDate()
        );
    };

    const handleRemindTask = async (task) => {
        if (isReminderSentToday(task.LastRmdrDT)) {
            toast.error("You can only send one reminder for this task per day");
            return;
        }

        try {
            setTaskActionLoading(prev => ({ ...prev, [task.TskID]: 'reminding' }));

            const taskData = {
                taskID: task.TskID,
                connID: task.ConnID || 0,
                pocUserID: task.UID,
                taskName: task.Ttl,
                description: task.Txt,
                priority: task.Pri,
                status: task.Sts,
                taskType: task.Typ,
                threads: task.TIDs
            };

            await remindTaskAPI(taskData);
            toast.success(`Reminder sent to ${getExecutiveName(task.UID)}.`);
        } catch (error) {
            console.error('Error sending reminder:', error);
            toast.error(
                error.message || 'Failed to send reminder. Please try again.'
            );
        } finally {
            setTaskActionLoading(prev => ({ ...prev, [task.TskID]: null }));
        }
    };

    // Generic action handler for tasks and threads
    const handleAction = async (item, action, type, shareByEmail = false, takeFeedback = false, rejReason = "", isMarkCold = false, shareByWhatsApp = false) => {
        if (!item) return;
        const id = type === 'task' ? item.TskID : item.ThreadID;
        const setLoading = type === 'task' ? setTaskActionLoading : setThreadActionLoading;
        setLoading(prev => ({ ...prev, [id]: `${action.toLowerCase()}ing` }));
        try {
            if (type === 'task') {
                const taskData = {
                    taskID: item.TskID,
                    connID: connection?.ConnID,
                    pocUserID: item.UID,
                    taskName: item.Ttl,
                    description: item.Txt,
                    priority: item.Pri,
                    status: item.Sts,
                    taskType: item.Typ,
                    threads: item.TIDs,
                    AgencyID: item.AgencyID || connection?.AgencyID,
                };
                if (action === 'DONE') {
                    await completeTaskAPI(taskData);
                    toast.success('Task completed.');
                } else {
                    // Assignee (not creator) declining the task is a Reject, not a Cancel
                    const myUserIDs = getMyAgenciesMappedIDs('UserID');
                    const taskCreatorID = Number(item.CreatedUID || item.UID || 0);
                    const isMyCreator = myUserIDs.includes(taskCreatorID);
                    const isMyAssignee = myUserIDs.includes(Number(item.UID)) || Number(item.UID) === Number(currentUser?.UserID);
                    const isReject = isMyAssignee && !isMyCreator;

                    if (isReject) {
                        await rejectTaskAPI(taskData);
                    } else {
                        await cancelTaskAPI(taskData);
                    }
                    toast.success(isReject ? 'Task rejected.' : 'Task cancelled.');
                }
            } else if (type === 'thread') {
                const threadData = {
                    threadID: item.ThreadID || item.threadID,
                    typeID: item.TypeID || item.typeID,
                    pocUserID: item.POCUserID,
                    doneNotes: `${action === 'DONE' ? 'Thread completed' : rejReason}`,
                    shareByEmail,
                    shareByWhatsApp,
                    takeFeedback,
                    connID: item?.ConnID,
                    currencySymbol: currencySymbol,
                    reference: item?.Reference,
                    bizProspect: item?.BizProspect,
                    mainProduct: item?.MainProduct,
                    isCold: isMarkCold,
                    connectionData: {
                        bizName: item?.connectionData?.BizName || connection?.BizName,
                        contactName: item?.connectionData?.ContactName || connection?.ContactName,
                        email: item?.connectionData?.Email || connection?.Email,
                        mobile: item?.connectionData?.Mobile || connection?.Mobile,
                        cc: item?.connectionData?.CC || connection?.CC
                    }
                };
                const result = await performThreadActionAPI(threadData, action);
                if (result.error == false && result.message == "Success") {
                    toast.success(`${getThreadNameFromTypeID(selectedThreadForAction?.TypeID, threadTypesData, "SnSng")} ${action === 'DONE' ? 'marked as done.' : 'cancelled.'}`);
                    if (action === 'DONE') {
                        const isSale = [2, 5].includes(Number(item.TypeID || item.typeID));
                        if (isSale) {
                            setCompletedThreadForAttachment(item);
                            setAttachDocDialog(true);
                        }
                    }
                }
            }
            if (refreshConnectionData) {
                await refreshConnectionData();
            }
        } catch (error) {
            console.error(`Error ${action.toLowerCase()}ing ${type}:`, error);
            toast.error(error.message || `Failed to ${action.toLowerCase()} ${type}. Please try again.`);
        } finally {
            setLoading(prev => ({ ...prev, [id]: null }));
            setDialogStates(prev => ({
                ...prev,
                [`confirm${type.charAt(0).toUpperCase() + type.slice(1)}CompleteDialog`]: false,
                [`confirm${type.charAt(0).toUpperCase() + type.slice(1)}CancelDialog`]: false
            }));
            type === 'task' ? setSelectedTaskForRecord(null) : setSelectedThreadForAction(null);
        }
    };

    const handleUploadAttachment = async (file) => {
        if (!file || !completedThreadForAttachment) return false;

        if (file.size > 1024 * 1024) {
            toast.error("File size must be up to 1 MB only.");
            return false;
        }

        try {
            dispatch(setGlobalLoading(true));
            const uploadResult = await apiService.uploadFile(file, "Threads", "both");

            if (uploadResult?.error) {
                toast.error(uploadResult?.message || "Failed to upload file");
                return false;
            }

            const uploadedFileName = uploadResult?.data?.relativePath;
            if (!uploadedFileName) {
                toast.error("Failed to get uploaded file path");
                return false;
            }

            const threadTypes = masterData?.threadTypes || threadTypesData || [];
            const targetData = {
                ...completedThreadForAttachment,
                threadID: completedThreadForAttachment.ThreadID || completedThreadForAttachment.threadID,
                typeID: completedThreadForAttachment.TypeID || completedThreadForAttachment.typeID,
                pocUserID: completedThreadForAttachment.POCUserID || completedThreadForAttachment.pocUserID,
                connID: completedThreadForAttachment.ConnID || completedThreadForAttachment.connID,
                thrFile: uploadedFileName
            };

            const result = await manageThreadAPI(targetData, 'EDIT', threadTypes);

            if (result?.error === false && result?.message === 'Success') {
                toast.success("Document attached successfully.");
                if (refreshConnectionData) {
                    await refreshConnectionData();
                }
                setAttachDocDialog(false);
                setCompletedThreadForAttachment(null);
                return true;
            } else {
                toast.error(result?.message || "Failed to save attachment to sale record.");
                return false;
            }
        } catch (err) {
            console.error("Error uploading attachment for completed sale:", err);
            toast.error("Failed to upload document.");
            return false;
        } finally {
            dispatch(setGlobalLoading(false));
        }
    };

    const handleItemAction = (action, item, type) => {
        if (action === 'RECORD_AND_COMPLETE' && item && type === 'task') {
            setDialogStates(prev => ({ ...prev, confirmCompleteDialog: false, recordEntry: true }));
        }
    };

    // Helper function to format task data
    const formatTaskData = (task) => {
        const dateObj = new Date(task?.DT);
        return {
            taskID: task.TskID,
            connID: connection?.ConnID,
            taskName: task.Ttl,
            description: task.Txt,
            dueDT: format(dateObj, 'yyyy-MM-dd'),
            dueTime: format(dateObj, 'HH:mm'),
            pocUserID: task.UID,
            priority: task.Pri,
            threads: task.TIDs ? task.TIDs.split(',') : '',
            status: task.Sts,
            taskType: task.Typ,
            connectionLocked: true,
            bizName: connection?.BizName || '',
            contactName: connection?.ContactName || '',
            repeatAfter: task.RepeatAfter || 0,
            repeatDT: task.RepeatDT || '',
        };
    };

    const openRepeatTaskEntry = (task) => {
        if (!task) return;
        setRepeatPrefillData({
            ...formatTaskData(task),
            taskID: 0,
            status: undefined,
            rmdrDT: undefined,
            dueDT: undefined,
            dueTime: undefined,
            connID: connection?.ConnID,
            connectionLocked: true,
            connectionLabel: 'Contact',
            secConnIDs: task.SCIDs ? task.SCIDs.split(",").map(id => Number(id)).filter(Boolean) : '',
        });
        setTaskEntryMode('repeat');
        setEditingTaskID(null);
        setDialogStates(prev => ({ ...prev, taskEntry: true }));
    };

    const refreshConnectionData = async () => {
        if (!connection?.ConnID) return;
        try {
            setLoadingRecords(true);
            const recordsData = await getConnectionDetailAPI(connID, '');
            setRecords(recordsData?.records || []);
            setTransactions(recordsData?.transactions || []);
            setContractsFromAPI(recordsData?.contracts || []);
            setOrdersHistory(recordsData?.ordersHistory.sort((a, b) => new Date(b.SinceDT) - new Date(a.SinceDT)) || []);
        } catch (error) {
            console.error("Error refreshing connection data:", error);
        } finally {
            setLoadingRecords(false);
        }
    };

    // Handle toggle cold contact status
    const handleToggleColdContact = async () => {
        try {
            const newColdStatus = (connection?.IsCold === 1 || connection?.IsCold === true) ? 0 : 1;
            const updateData = {
                ConnID: connection.ConnID,
                IsCold: newColdStatus
            };

            await manageConnectionAPI(updateData, 'COLD');

            const message = newColdStatus === 1 ? 'Marked as cold contact' : 'Marked as active contact';
            toast.success(message);

            await refreshConnectionData();
        } catch (error) {
            console.error('Error toggling cold contact status:', error);
            toast.error('Failed to update contact status');
        }
    };
    // Set agency context on apiService when connection changes
    useEffect(() => {
        if (isOpen && connection?.AgencyID) {
            apiService.setAgencyContext(connection.AgencyID);
        }
        return () => {
            apiService.clearAgencyContext();
        };
    }, [isOpen, connection?.AgencyID]);

    // Load records and transactions on dialog open
    useEffect(() => {
        if (!isOpen || !connID || !connection) {
            setRecords([]);
            setTransactions([]);
            setContracts([]);
            setAccessUserIDs([]);
            setLoadingRecords(false);
            return;
        }

        // Initialize access IDs from connection data
        if (connection.POCUserIDs) {
            const ids = connection.POCUserIDs.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            setAccessUserIDs(ids);
        }

        const loadData = async () => {
            try {
                setLoadingRecords(true);
                const data = await getConnectionDetailAPI(connID, '');
                setFeedbackThreadDetail({
                    thrTypeID: data?.rawData?.ThrTypeID || 0,
                    thrReference: data?.rawData?.ThrReference || 0,
                    thrProduct: data?.rawData?.ThrProduct || '',
                    thrFeedbackDT: data?.rawData?.ThrFeedbackDT || null,
                    thrFailureRsn: data?.rawData?.ThrFailureRsn || '',
                });
                setRecords(data?.records || []);
                setTransactions(data?.transactions || []);
                setContractsFromAPI(data?.contracts || []);
                setOrdersHistory(data?.ordersHistory.sort((a, b) => new Date(b.SinceDT) - new Date(a.SinceDT)) || []);
            } catch (error) {
                console.error("Error loading records:", error);
                setRecords([]);
                setTransactions([]);
                setOrdersHistory([]);
            } finally {
                setLoadingRecords(false);
            }
        };
        loadData();
    }, [isOpen, connID, connection?.ConnID]);

    // Merge contracts from Redux and API
    useEffect(() => {
        const reduxContracts = contractsFromRedux.filter(contract =>
            (contract.ConnID === connection?.ConnID || contract.ConnID === connID) &&
            contract.StatusID === 0  // Only show active contracts
        );

        const mergedContracts = [...reduxContracts];

        // Remove duplicates based on ContractID and ensure all contracts have StatusID === 0
        const uniqueContracts = mergedContracts.filter((contract, index, arr) =>
            arr.findIndex(c => c.ContractID === contract.ContractID) === index &&
            contract.StatusID === 0
        );

        setContracts(uniqueContracts);
    }, [contractsFromRedux, connection?.ConnID, connID]);

    // Removed duplicate fetchOrdersData logic - now handled in loadData/refreshConnectionData via getConnectionDetailAPI

    const threads = getThreadsData();

    // Update selectedThread when data changes (e.g. after edit)
    useEffect(() => {
        if (selectedThread && dialogStates.threadDetail) {
            // Try to find updated thread in active threads
            const updatedActive = threads.find(t => t.ThreadID === selectedThread.ThreadID);
            if (updatedActive) {
                setSelectedThread(updatedActive);
                return;
            }
            // Try to find updated thread in orders history
            const updatedOrder = ordersHistory.find(o => o.ThreadID === selectedThread.ThreadID);
            if (updatedOrder) {
                setSelectedThread(updatedOrder);
                return;
            }
        }
    }, [threads, ordersHistory, dialogStates.threadDetail]);

    const recordTypes = useSelector((state) => state.masterData?.recordTypes || []);
    const interactionRecordTypeNames = useMemo(() => {
        return new Set(
            recordTypes
                .filter(rt => rt.IsInxn === true || rt.IsInxn === 1)
                .map(rt => rt.Typ)
        );
    }, [recordTypes]);

    const sortedRecords = records.filter(record => record && record?.DT).sort((a, b) => new Date(b?.DT) - new Date(a?.DT));
    const lastRecord = useMemo(() => {
        if (sortedRecords.length === 0 || interactionRecordTypeNames.size === 0) return null;
        return sortedRecords.find(record => interactionRecordTypeNames.has(record.Typ)) || null;
    }, [sortedRecords, interactionRecordTypeNames]);

    // Filter records to show only those assigned to the user (matching RIDs)
    const userAssignedRecords = records.filter(record =>
        record && record.RID && userPendingRIDs.includes(String(record.RID))
    );

    const callCount = userAssignedRecords.filter(record => record && record.Typ === 'Call' && record.Txt === 'Dialed Call.').length;
    const messageCount = userAssignedRecords.filter(record => record && record.Typ === 'Message' && record.Txt === 'Sent Message.').length;

    const cntInqs = threads?.length || 0;
    const cntOrds = allOrders?.length || 0;

    // Function which will handle sentiment request
    const handleFdbkRequest = async (targetItem = null) => {
        // If targetItem is a thread/order, trigger thread feedback request (FDBKREQ on module 1)
        const isThreadItem = Boolean(targetItem && (targetItem.ThreadID || targetItem.threadID || targetItem.TypeID || targetItem.typeID));
        if (isThreadItem) {
            dispatch(setGlobalLoading(true));
            try {
                const threadData = {
                    ...targetItem,
                    threadID: targetItem.ThreadID || targetItem.threadID || targetItem.actionID,
                    typeID: targetItem.TypeID || targetItem.typeID || 2,
                    pocUserID: targetItem.POCUserID || targetItem.pocUserID || 0,
                    shareByWhatsApp: true,
                    shareByEmail: false,
                    takeFeedback: true,
                    connID: targetItem.ConnID || targetItem.connID || connection?.ConnID,
                    reference: targetItem.Reference || targetItem.reference,
                    product: targetItem.MainProduct || targetItem.mainProduct || targetItem.Product || targetItem.product,
                    connectionData: {
                        bizName: targetItem.BizName || targetItem.bizName || targetItem.connectionData?.bizName || targetItem.connectionData?.BizName || connection?.BizName,
                        contactName: targetItem.ContactName || targetItem.contactName || targetItem.ContactPerson || targetItem.connectionData?.contactName || targetItem.connectionData?.ContactName || connection?.ContactName,
                        email: targetItem.Email || targetItem.email || targetItem.connectionData?.email || targetItem.connectionData?.Email || connection?.Email,
                        mobile: targetItem.Mobile || targetItem.mobile || targetItem.connectionData?.mobile || targetItem.connectionData?.Mobile || connection?.Mobile,
                        cc: targetItem.CC || targetItem.cc || targetItem.connectionData?.cc || targetItem.connectionData?.CC || connection?.CC
                    }
                };

                const result = await performThreadActionAPI(threadData, 'FDBKREQ');
                if (result.error === false) {
                    toast.success("Feedback request sent on WhatsApp.");
                } else {
                    toast.error(result.message || "Failed to send feedback request.");
                }
            } catch (err) {
                console.error('Error while sending feedback request', err);
                toast.error("Failed to send feedback request.");
            } finally {
                dispatch(setGlobalLoading(false));
            }
            return;
        }

        const sendByTool = {
            sendByEmail: false,
            sendByWsp: true
        }

        const connMobile = targetItem?.Mobile || targetItem?.connMobile || targetItem?.connectionData?.Mobile || connection?.Mobile || "";
        const connCC = targetItem?.CC || targetItem?.connCC || targetItem?.connectionData?.CC || connection?.CC || "91";
        const connIDVal = targetItem?.ConnID || targetItem?.connID || connection?.ConnID;
        const bizNameVal = targetItem?.BizName || targetItem?.bizName || targetItem?.connectionData?.BizName || connection?.BizName || "";
        const contactNameVal = targetItem?.ContactName || targetItem?.contactName || targetItem?.ContactPerson || targetItem?.connectionData?.ContactName || connection?.ContactName || "";
        const emailVal = targetItem?.Email || targetItem?.email || targetItem?.connectionData?.Email || connection?.Email || "";

        const apiData = {
            connID: connIDVal,
            connMobile: connMobile,
            connCC: connCC,
            bizName: bizNameVal,
            contactPerson: contactNameVal,
            email: emailVal,
            userMobile: currentUser.Mobile,
            username: currentUser.Name,
            userID: currentUser.UserID,
            sendByEmail: sendByTool.sendByEmail,
            sendByWsp: sendByTool.sendByWsp
        }
        dispatch(setGlobalLoading(true));
        try {
            const result = await manageFdbkRequestAPI(apiData);
            if (result.error == false) {
                if (sendByTool.sendByEmail) {
                    toast.success("Email sent for sentiment request.")
                }
                else {
                    toast.success("Whatsapp message sent for sentiment request.")
                }
            }
        } catch (err) {
            console.error('Error while sending sentiment request', err)
        } finally {
            dispatch(setGlobalLoading(false));
        }
    }

    // Company info section component
    const CompanyInfoSection = ({ onScrollToSection, refs }) => (
        <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
            <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-3">
                    {connection?.BizName ? <Building2 className="h-5 w-5 text-sahi-primary-color" /> : <User className="h-5 w-5 text-sahi-primary-color" />}
                    <h2 className={`${styleClasses.sectionTitle} ${styleClasses.textColor}`}>
                        {connection?.BizName || connection?.ContactName || "Company Name"}
                    </h2>
                    {/* <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleToggleColdContact}
                        className="h-5 w-5 p-0 hover:bg-transparent flex-shrink-0"
                    >
                        <GiIceCube
                            className={`h-4 w-4 flex-shrink-0 cursor-pointer transition-colors ${connection?.IsCold === 1 || connection?.IsCold === true
                                ? 'text-blue-500'
                                : 'text-zinc-400'
                                }`}
                        />
                    </Button> */}
                </div>
                <div className={`${styleClasses.flexBetween} flex-wrap gap-2`}>
                    <div className="flex gap-1 flex-wrap font-bold items-center">
                        {getConnectionType()?.map((type, index) => (
                            <StatusBadge key={index} className="pt-2 font-semibold">
                                {type}
                            </StatusBadge>
                        ))}
                        {contacts?.length > 1 && (
                            <StatusBadge
                                onClick={() => scrollToSection(contactsRef)}
                                className="pt-2 font-semibold cursor-pointer hover:bg-zinc-200 transition-colors"
                            >
                                {contacts?.length || 0} {contacts?.length > 1 ? "Contact persons" : "Contact person"}
                            </StatusBadge>
                        )}
                    </div>
                    {contacts?.length === 1 && (
                        <ContactActions
                            connectionData={{
                                mobile: contacts[0]?.MNo || "",
                                email: contacts[0]?.Eml || "",
                                cc: contacts[0]?.CC || "91",
                                name: contacts[0]?.CNm || "",
                                contName: contacts[0]?.CNm || ""
                            }}
                            connID={connection?.ConnID}
                            showDashes={false}
                            onRecordAdded={refreshConnectionData}
                        />
                    )}
                    {/* Mobile Contact Actions Dialog from thread params */}
                    {isMobileContactActionsOpen && contacts?.length > 0 && (
                        <ContactActions
                            open={isMobileContactActionsOpen}
                            onOpenChange={onCloseContactActions}
                            connectionData={{
                                mobile: contacts[0]?.MNo || "",
                                email: contacts[0]?.Eml || "",
                                cc: contacts[0]?.CC || "91",
                                name: contacts[0]?.CNm || "",
                                contName: contacts[0]?.CNm || ""
                            }}
                            connID={connection?.ConnID}
                            onRecordAdded={refreshConnectionData}
                        />
                    )}
                </div>
                {(getNextTaskDT() || connection?.LastRecDT) && (
                    <div className={`${styleClasses.flexBetween} flex-wrap pt-1`}>
                        <div className="flex gap-1 flex-wrap flex-row">
                            {getNextTaskDT() && (
                                <StatusBadge>
                                    Next: <span className="text-sahi-green font-semibold">{getRelativeDateText(getNextTaskDT(), false, false, false)}</span>
                                </StatusBadge>
                            )}
                            {connection?.LastRecDT && (
                                <StatusBadge>
                                    Last: <span className="text-sahi-green font-semibold">{getRelativeDateText(connection.LastRecDT, false, false, true)}</span>
                                </StatusBadge>
                            )}
                        </div>
                    </div>
                )}
                <ConditionalDiv className="flex flex-wrap gap-2 pt-3">
                    {!isSupplierOrOther && (
                        isInqOnlyMode ? (
                            (cntInqs > 0 || cntOrds > 0) ? (
                                <div className="flex gap-2">
                                    {/* Inquiries Button */}
                                    {cntInqs > 0 && (
                                        <Button
                                            onClick={() => onScrollToSection(refs.threadsRef)}
                                            className={`bg-sahi-orange text-xs px-1.5 py-1 h-auto flex-1 hover:bg-white hover:text-sahi-orange border hover:border-sahi-orange gap-1`}
                                        >
                                            <span className="text-xs">{cntInqs}</span> {cntInqs > 1 ? "Inquiries" : "Inquiry"}
                                        </Button>
                                    )}

                                    {cntOrds > 0 && (
                                        <Button
                                            onClick={() => onScrollToSection(refs.ordersRef)}
                                            className={`bg-sahi-green text-xs px-1.5 py-1 h-auto flex-1 hover:bg-white hover:text-sahi-green border hover:border-sahi-green gap-1`}
                                        >
                                            <span className="text-xs">{cntOrds}</span> {cntOrds > 1 ? "Sales" : "Sale"}
                                        </Button>
                                    )}
                                </div>
                            ) : null
                        ) : (
                            threads?.length !== 0 && (
                                <Button
                                    onClick={() => onScrollToSection(refs.threadsRef)}
                                    className={`bg-sahi-orange text-xs px-1.5 py-1 h-auto hover:bg-white hover:text-sahi-orange border hover:border-sahi-orange`}
                                >
                                    <span>{threads?.length || 0}</span>{" "}
                                    {threads?.length > 1
                                        ? currActiveThreadType.typPlr
                                        : currActiveThreadType.typSng}
                                </Button>
                            )
                        )
                    )}
                    {tasks?.length != 0 && (
                        <Button
                            onClick={() => onScrollToSection(refs.tasksRef)}
                            className={`bg-sahi-dark-blue text-xs px-1.5 py-1 h-auto hover:bg-white hover:text-sahi-dark-blue border hover:border-sahi-dark-blue`}
                        >
                            <span>{tasks?.length || 0}</span> {tasks?.length > 1 ? "Tasks" : "Task"}
                        </Button>
                    )}
                    {transactions?.length != 0 && (showModule.transactions) && (
                        <Button
                            onClick={() => onScrollToSection(refs.transactionsRef)}
                            className={`bg-sahi-red text-xs px-1.5 py-1 h-auto`}
                        >
                            <span>{transactions?.length || 0}</span> {transactions?.length > 1 ? "Xns" : "Xn"}
                        </Button>
                    )}
                    {contracts?.length != 0 && (showModule.contracts) && (
                        <Button
                            onClick={() => onScrollToSection(refs.contractsRef)}
                            className={`bg-sahi-primary-color text-xs px-1.5 py-1 h-auto`}
                        >
                            <span>{contracts?.length || 0}</span> {contracts?.length > 1 ? "Contracts" : "Contract"}
                        </Button>
                    )}
                </ConditionalDiv>
            </CardContent>
        </Card>
    );

    // Contacts section component
    const ContactsSection = () => (
        <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
            <CardContent className="p-0">
                <SectionHeader
                    title={
                        <span className="inline-flex items-center gap-1.5">
                            <span>{contacts.length > 1 ? "Contact Persons" : "Contact Person"}</span>
                            {contacts.length > 1 && (
                                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-zinc-100 text-zinc-700">
                                    {contacts.length}
                                </span>
                            )}
                        </span>
                    }
                    onAdd={isOwnAgencyConnection ? () => setDialogStates((prev) => ({ ...prev, otherContacts: true })) : undefined}
                    customButtons={
                        isOwnAgencyConnection ? (
                            <div
                                onClick={() => setDialogStates((prev) => ({ ...prev, editContactsDialog: true }))}
                                className="cursor-pointer"
                            >
                                <Pencil className="h-4 w-4" />
                            </div>
                        ) : null
                    }
                    addLabel="Add"
                />

                {contacts.length > 0 ? (
                    <div className="space-y-3 mt-3 p-3 pt-0">
                        {contacts.map((contact, index) => (
                            <ContactInfo
                                key={contact.CID || index}
                                contact={contact}
                                isPrimary={index === 0}
                                connection={connection}
                                onClick={() => {
                                    if (contact?.Eml !== '' || contact?.MNo !== '') {
                                        setSelectedContactForDetail({ ...contact, isPrimary: index === 0 });
                                        setDialogStates(prev => ({ ...prev, contactDetail: true }));
                                    }
                                }}
                            />
                        ))}
                    </div>
                ) : (
                    <NoDataCard
                        Icon={User}
                        title="No contacts found."
                        desc="Click to add a contact."
                        onClick={() => isOwnAgencyConnection && setDialogStates((prev) => ({ ...prev, otherContacts: true }))}
                    />
                )}
            </CardContent>
        </Card>
    );

    const isTodayOrYesterday = (dateString) => {
        if (!dateString) return false;
        const recordDate = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const recordDay = recordDate.toDateString();
        const todayDay = today.toDateString();
        const yesterdayDay = yesterday.toDateString();

        return recordDay === todayDay || recordDay === yesterdayDay;
    };

    // Last interaction section component
    const LastInteractionSection = () => {
        const [isExpanded, setIsExpanded] = useState(false);
        const callCount = records.filter(record => record && record.Typ === 'Call' && record.Txt === 'Dialed Call.' && isTodayOrYesterday(record.DT)).length;
        const messageCount = records.filter(record => record && record.Typ === 'Message' && record.Txt === 'Sent Message.' && isTodayOrYesterday(record.DT)).length;
        return (
            <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
                <CardContent className="p-0">
                    <SectionHeader
                        title="Last Interaction"
                        onAdd={lastRecord && hasAgencyAccess ? () => setDialogStates((prev) => ({ ...prev, recordEntry: true })) : undefined}
                        onHistory={lastRecord ? () => setDialogStates((prev) => ({ ...prev, recordHistory: true })) : undefined}
                        addLabel="Add"
                        historyButton={!!lastRecord}
                    />

                    {loadingRecords ? (
                        <div className="p-4">
                            <div className="animate-pulse">
                                <div className="h-4 bg-zinc-200 rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-zinc-200 rounded w-1/2"></div>
                            </div>
                        </div>
                    ) : lastRecord ? (
                        <div className="p-3 pt-0 flex flex-col gap-2.5">
                            <div
                                className="bg-zinc-50 rounded-lg border border-zinc-200 shadow-xs p-3 py-2  cursor-pointer hover:border-zinc-300 hover:shadow-sm transition-all"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                <div className="mb-2">
                                    <p className={`text-sm ${styleClasses.textColor} leading-normal`}>
                                        {isExpanded ? lastRecord.Txt : formatRecordText(lastRecord.Txt)}
                                    </p>
                                </div>
                                <div className={`${styleClasses.flexBetween} text-xs pt-2 border-t border-zinc-200`}>
                                    <span className="text-zinc-500 font-medium flex items-center gap-1">
                                        @ {getRelativeDateText(lastRecord?.DT, false, false, false)}
                                    </span>
                                    <span className="text-zinc-500 flex items-center gap-1 font-medium">
                                        <User className="h-3 w-3 text-zinc-400" />
                                        {getExecutiveName(lastRecord.UID)}
                                    </span>
                                </div>
                            </div>
                            {(callCount > 0 || messageCount > 0) && (
                                <div>
                                    <p className="text-xs text-sahi-green font-semibold mb-1.5 uppercase tracking-wider">
                                        Details Pending
                                    </p>
                                    <div className={`flex gap-2 ${callCount > 0 && messageCount > 0 ? 'w-full' : 'w-1/2'}`}>
                                        {callCount > 0 && (
                                            <Button
                                                size="sm"
                                                className="h-auto py-2 px-2.5 text-xs bg-zinc-100 border border-zinc-200 text-foreground hover:bg-zinc-200 flex-1 flex items-center justify-between rounded-lg"
                                                onClick={() => setDialogStates((prev) => ({ ...prev, recordHistory: true, recordTypeFilter: 'Call' }))}
                                            >
                                                <span className="text-xs">Calls <span className="text-zinc-800 font-semibold">({callCount})</span></span>
                                                <Edit2 className="h-3 w-3 text-sahi-blue flex-shrink-0" />
                                            </Button>
                                        )}
                                        {messageCount > 0 && (
                                            <Button
                                                size="sm"
                                                className="h-auto py-2 px-2.5 text-xs bg-zinc-100 border border-zinc-200 text-foreground hover:bg-zinc-200 flex-1 flex items-center justify-between rounded-lg"
                                                onClick={() => setDialogStates((prev) => ({ ...prev, recordHistory: true, recordTypeFilter: 'Message' }))}
                                            >
                                                <span className="text-xs">Messages <span className="text-zinc-800 font-semibold">({messageCount})</span></span>
                                                <Edit2 className="h-3 w-3 text-sahi-blue flex-shrink-0" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {hasAgencyAccess && (
                                <Button
                                    className="w-full h-9 py-2 px-4 bg-[#0F1E36] hover:bg-[#1a2e4c] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm transition-colors"
                                    onClick={() => setDialogStates((prev) => ({ ...prev, recordEntry: true }))}
                                >
                                    <Plus className="h-4 w-4" />
                                    Enter Interaction
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="p-3 pt-0">
                            <div className="rounded-lg bg-[#E8EDF2] p-4 flex flex-col">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-md bg-[#D0DBE5] flex items-center justify-center flex-shrink-0 text-[#1E293B] mt-0.5">
                                        <MessageSquare className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-semibold text-zinc-800">
                                            No interactions recorded yet
                                        </h4>
                                        <p className="text-xs text-zinc-600 mt-0.5 leading-normal">
                                            Log a call, meeting or note to build this contact's history
                                        </p>
                                    </div>
                                </div>
                                {hasAgencyAccess && (
                                    <Button
                                        className="w-full h-9 mt-3.5 py-2 px-4 bg-[#0F1E36] hover:bg-[#1a2e4c] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm transition-colors"
                                        onClick={() => setDialogStates((prev) => ({ ...prev, recordEntry: true }))}
                                    >
                                        <Plus className="h-4 w-4" />
                                        Enter Interaction
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    };

    const FeedbackSection = ({ feedbackScore, feedbackNotes, failureReason, threadDetail = null }) => {
        // Don't render if feedbackScore is 0 or falsy
        if (!feedbackScore || feedbackScore === 0) {
            return null;
        }

        return (
            <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
                <CardContent className="p-0">
                    <SectionHeader
                        title="Sentiment"
                    />
                    <div className="p-3 pt-0">
                        <div className="bg-zinc-50 rounded-lg border border-zinc-200 shadow-xs divide-y divide-zinc-200">
                            {/* Top row - Sentiment emoji, failure reason & notes */}
                            <div className="p-2.5 flex items-center gap-2">
                                <span className="inline-flex items-center shrink-0">
                                    {sentimentEmoji(feedbackScore, "!h-5 !w-5")}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm ${styleClasses.textColor} leading-normal truncate`}>
                                        {failureReason && failureReason.trim() && feedbackScore >= 1 && feedbackScore <= 3 && (
                                            <span className="font-semibold text-gray-900 mr-1">{failureReason} -</span>
                                        )}
                                        <span className="text-zinc-700">{feedbackNotes}</span>
                                    </p>
                                </div>
                            </div>

                            {/* Bottom row - Thread icon, reference, product & feedback date */}
                            {threadDetail && threadDetail.thrTypeID > 0 && (
                                <div className="p-2.5 flex items-center justify-between text-xs">
                                    <span className="text-zinc-500 font-medium flex items-center gap-1.5 truncate">
                                        <span className="inline-flex items-center shrink-0">
                                            {getThreadTypeIcon(threadDetail.thrTypeID, threadTypesData, "h-4 w-4")}
                                        </span>
                                        <span className="truncate">
                                            {threadDetail.thrReference !== 0 && `#${threadDetail.thrReference} - `}
                                            {threadDetail.thrProduct}
                                        </span>
                                    </span>
                                    {threadDetail.thrFeedbackDT && (
                                        <span className="text-zinc-500 font-medium shrink-0 ml-2">
                                            @ {getRelativeDateText(threadDetail.thrFeedbackDT, false, false, false)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    // Single Thread Details Card Component (matches desktop using ThrDetails components)
    const SingleThreadDetailsCard = ({ thread }) => {
        const threadTypeMeta = getThreadTypeMeta(thread?.TypeID, threadTypesData);
        const canShowReassignButton = users.length > 1;

        // Transform thread data to match desktop format
        const transformedThreadData = {
            threadID: thread.ThreadID,
            typeID: thread.TypeID,
            agencyID: thread.AgencyID,
            connID: thread.ConnID,
            mainProduct: thread.MainProduct,
            mainQty: thread.MainQty,
            model: thread.Model,
            pocUserID: thread.POCUserID,
            pocUserName: getExecutiveName(thread.POCUserID),
            statusID: thread.StatusID,
            dueDT: thread.DueDT,
            dueDTF: getDateWithoutTime(thread.DueDT),
            sinceDT: thread.SinceDT,
            sinceDTF: getDateWithoutTime(thread.SinceDT),
            reference: thread.Reference,
            bizProspect: thread.BizProspect,
            currStageID: thread.CurrStageID,
            stageInfo: null,
            notes: thread.Notes,
            thrFile: thread.ThrFile || thread.thrFile || thread.thrfile,
            source: thread.Source,
            feedbackNotes: thread.FdbkNotes,
            feedbackScore: thread.FdbkScore,
            failureReason: thread.FailureReason,
            statusHist: thread.StatusHist || thread.statusHist,
            connectionData: {
                bizName: connection?.BizName,
                contactName: connection?.ContactName,
                cc: connection?.CC,
                mobile: connection?.Mobile,
                email: connection?.Email,
                ConnType: connection?.ConnType
            },
            // customFields: thread?.CustomFields ? JSON.parse(thread?.CustomFields) : [], //for local
            customFields: (thread?.ExtraFields && thread.ExtraFields !== "[]") ? JSON.parse(thread.ExtraFields) : (thread?.CustomFields && thread.CustomFields !== "[]" ? JSON.parse(thread.CustomFields) : [])
        };

        const handleReassign = () => {
            setSelectedThreadForAction(transformedThreadData);
            setDialogStates(prev => ({ ...prev, reassignDialog: true }));
        };

        const handleUpdateStatus = async (stageID, markAsDone = false, shareByEmail = false, takeFeedback = false, completionNote = '') => {
            try {
                dispatch(setGlobalLoading(true));
                const thrdApiData = {
                    ...transformedThreadData,
                    shareByEmail,
                    takeFeedback,
                    doneNotes: completionNote,
                    currencySymbol: currencySymbol
                };
                const result = markAsDone
                    ? await performThreadActionAPI(thrdApiData, 'DONE')
                    : await updateThreadStageAPI(thrdApiData, stageID, markAsDone);

                if (result.error === false) {
                    toast.success(markAsDone
                        ? `${getThreadNameFromTypeID(transformedThreadData?.typeID, threadTypesData, "SnSng")} marked as done.`
                        : `${getThreadNameFromTypeID(transformedThreadData?.typeID, threadTypesData, "SnSng")} status updated.`
                    );
                    handleThreadUpdated();
                }
            } catch (error) {
                console.error('Error updating thread status:', error);
                toast.error('Failed to update thread status');
            } finally {
                dispatch(setGlobalLoading(false));
            }
        };

        const handleCloseThread = async () => {
            setSelectedThreadForAction(transformedThreadData);
            setDialogStates(prev => ({ ...prev, confirmThreadCancelAlert: true }));
        };

        const handleEditThread = () => {
            setEditingThreadID(transformedThreadData.threadID);
            setEditingThreadTypeID(transformedThreadData.typeID);
            setDialogStates(prev => ({ ...prev, threadEntry: true, prefilledData: transformedThreadData }));
        };

        return (
            <div className="space-y-0">
                {/* Thread Details Info */}
                <ThrDetailsInfo
                    threadData={transformedThreadData}
                    threadTypeMeta={threadTypeMeta}
                    onReassign={isOwnAgencyConnection ? handleReassign : undefined}
                    canShowAssignedTo={isOwnAgencyConnection && canShowReassignButton}
                    canShowReassignButton={isOwnAgencyConnection && canShowReassignButton}
                />

                {/* Sentiment / Feedback Section */}
                <FeedbackSection
                    feedbackScore={transformedThreadData?.feedbackScore}
                    feedbackNotes={transformedThreadData?.feedbackNotes}
                    failureReason={transformedThreadData?.failureReason}
                />

                {/* Status Section */}
                <ThrDetailsStatus
                    threadData={transformedThreadData}
                    threadTypeMeta={threadTypeMeta}
                    onStageUpdate={isOwnAgencyConnection ? handleUpdateStatus : undefined}
                />

                {/* Actions Section */}
                {isOwnAgencyConnection &&
                    <div className="p-4">
                        <ThrDetailsActions
                            threadData={transformedThreadData}
                            onUpdateStatus={handleUpdateStatus}
                            onCloseThread={(rejReason, isMarkCold) => {
                                handleAction(transformedThreadData, 'CANCEL', 'thread', false, false, rejReason, isMarkCold);
                            }}
                            onMarkFake={(threadData, reasonText, isMarkCold) => {
                                handleAction(transformedThreadData, 'CANCEL', 'thread', false, false, reasonText, isMarkCold);
                            }}
                            onReassign={() => {
                                setSelectedThreadForAction(transformedThreadData);
                                setDialogStates(prev => ({ ...prev, reassignDialog: true }));
                            }}
                            onConvertInquiry={() => {
                                setSelectedThreadForAction(transformedThreadData);
                                setEditingThreadTypeID(2);
                                setDialogStates(prev => ({ ...prev, convertToSales: true }));
                            }}
                            canShowReassignButton={canShowReassignButton}
                        />
                    </div>
                }
            </div>
        );
    };

    // Threads section component
    const ThreadsSection = () => (
        <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing} !gap-0`}>
            <div className="p-0">
                <SectionHeader
                    className={threads.length === 1 ? '!mb-0' : ''}
                    title={
                        threads.length === 1 ? (
                            `${(isInqOnlyMode ? 'Inquiry' : 'Thread')} Details`
                        ) : (
                            <span className="inline-flex items-center gap-1.5">
                                <span>{currActiveThreadType.typPlr}</span>
                                {threads.length > 0 && (
                                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-[#FDF0E6] text-[#B85006]">
                                        {threads.length}
                                    </span>
                                )}
                            </span>
                        )
                    }
                    customButtons={
                        threads.length > 0 ? (
                            <>
                                {isOwnAgencyConnection && (activeThreadTypesArr.length > 2 ? <ThreadDropdown
                                    onThreadTypeSelect={(threadTypeID) => {
                                        setDataStates((prev) => ({ ...prev, selectedThreadType: threadTypeID }));
                                        setDialogStates((prev) => ({ ...prev, threadEntry: true, prefilledData: null }));
                                    }}
                                    iconButton={false}
                                    minimalIcon={true}
                                    connectionType={connection?.ConnType}
                                    className="!h-5 !w-5"
                                    title="Add"
                                /> :
                                    <Plus
                                        className={`h-6 w-6 text-zinc-600 hover:text-zinc-800 cursor-pointer transition-colors !h-5 !w-5`}
                                        data-thread-dropdown-trigger
                                        onClick={() => {
                                            setDataStates((prev) => ({ ...prev, selectedThreadType: 1 }));
                                            setDialogStates((prev) => ({ ...prev, threadEntry: true, prefilledData: null }));
                                        }}
                                    />)}
                                {isOwnAgencyConnection && <div
                                    onClick={() => {
                                        onClose();
                                        navigate(`/threads-report?tID=1&cid=${connection?.ConnID}`);
                                    }}
                                    className="cursor-pointer"
                                >
                                    <History className="h-4 w-4" />
                                </div>}
                                {threads.length === 1 && canShowEditButton && threads[0]?.CurrStageID !== 7 ? (
                                    isOwnAgencyConnection ? <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setEditingThreadID(threads[0].ThreadID);
                                            setEditingThreadTypeID(threads[0].TypeID);
                                            setDataStates((prev) => ({ ...prev, selectedThreadType: threads[0].TypeID }));
                                            setDialogStates(prev => ({ ...prev, threadEntry: true }));
                                        }}
                                        className="h-5 w-5 p-0 text-sahi-blue flex-shrink-0"
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button> : null
                                ) : null}
                            </>
                        ) : null
                    }
                />

                {threads.length === 1 && threads[0]?.MainProduct && (
                    <div className="inline-flex items-center gap-2 px-2.5 md:px-3 rounded text-xs md:text-sm ml-1">
                        <div className="inline-flex items-center gap-1 min-w-0 flex-wrap">
                            <span className="whitespace-nowrap text-zinc-600">
                                for {threads[0]?.MainProduct}
                            </span>
                            {threads[0]?.BizProspect > 0 && (
                                <span className="text-sahi-green font-semibold">
                                    ({currencySymbol} {formatAmount(threads[0]?.BizProspect, agencyDB.CountryID)?.toLocaleString("en-IN") || "0"})
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <CardContent className="p-0">
                {threads.length === 1 ? (
                    <div className="flex flex-col pt-0">
                        <SingleThreadDetailsCard thread={threads[0]} />
                    </div>
                ) : threads.length > 0 ? (
                    <div className="space-y-3 p-3 pt-0">
                        {threads.map((thread, index) => (
                            <ThreadCard key={thread.ThreadID || index} thread={thread} />
                        ))}
                        {isOwnAgencyConnection && (
                            <Button
                                className="w-full h-9 py-2 px-4 bg-[#B85006] hover:bg-[#a04300] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm transition-colors"
                                onClick={() => setDialogStates((prev) => ({ ...prev, threadEntry: true, prefilledData: null }))}
                            >
                                <Plus className="h-4 w-4" />
                                Enter {isInqOnlyMode ? 'Inquiry' : 'Thread'}
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="p-3 pt-0">
                        <div className="rounded-lg bg-[#FDF0E6] p-4 flex flex-col">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-md bg-[#FCD8BF] flex items-center justify-center flex-shrink-0 text-[#B85006] mt-0.5">
                                    <FileText className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-zinc-800">
                                        {isInqOnlyMode ? "No inquiries found" : `No ${currActiveThreadType.typPlrLower} found`}
                                    </h4>
                                    <p className="text-xs text-zinc-600 mt-0.5 leading-normal">
                                        Track a product or service this contact is interested in
                                    </p>
                                </div>
                            </div>
                            {isOwnAgencyConnection && (
                                <Button
                                    className="w-full h-9 mt-3.5 py-2 px-4 bg-[#B85006] hover:bg-[#a04300] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm transition-colors"
                                    onClick={() => setDialogStates((prev) => ({ ...prev, threadEntry: true, prefilledData: null }))}
                                >
                                    <Plus className="h-4 w-4" />
                                    Enter {isInqOnlyMode ? 'Inquiry' : 'Thread'}
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );

    // Thread card component
    const ThreadCard = ({ thread }) => (
        <div
            className="bg-zinc-50 rounded-lg border border-zinc-200 shadow-xs w-full flex flex-col justify-between overflow-hidden hover:border-zinc-300 hover:shadow-sm transition-all cursor-pointer"
            onClick={() => {
                setSelectedThread(thread);
                setDialogStates(prev => ({ ...prev, thrDetails: true }))
            }}
        >
            <div className="p-3 flex flex-col gap-1.5">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-zinc-800 truncate">
                            {thread.MainProduct || thread.Model || ""}
                        </h3>
                        {thread.ContactPerson && (
                            <p className="text-xs text-zinc-500 mt-0.5 truncate font-medium">
                                {thread.ContactPerson}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col items-end text-right flex-shrink-0">
                        {getActiveThreadTypesArr.length > 2 && (
                            <div className="text-xs flex items-center gap-1 text-zinc-600 font-medium mb-1">
                                <ThreadSahiIcon className="h-3.5 w-3.5" tID={thread.TypeID} />
                                {getThreadNameFromTypeID(thread.TypeID || thread.typeID, threadTypesData)}
                            </div>
                        )}
                        <span className="text-sm font-semibold text-zinc-700">
                            {thread.BizProspect > 0 ? (
                                <>
                                    {currencySymbol} {thread.BizProspect?.toLocaleString("en-IN")}
                                </>
                            ) : (
                                "--"
                            )}
                        </span>
                    </div>
                </div>

                <div className="flex items-center justify-between pt-0.5 text-xs text-zinc-500">
                    {Number(thread.MainQty) > 1 ? (
                        <span className="flex items-center gap-1 text-xs text-zinc-500">
                            <Package className="h-3 w-3 text-zinc-400" />
                            <span>Qty: <strong className="text-zinc-700 font-medium">{thread.MainQty}</strong></span>
                        </span>
                    ) : <span />}
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                        <Calendar className="h-3 w-3 text-zinc-400" />
                        <span>Recd: <strong className="text-zinc-700 font-medium">
                            {thread.SinceDT || thread.CreatedDT ? getTableDateOrWeekday(thread.SinceDT || thread.CreatedDT) : (thread.LastRecordDT ? timeAgo(thread.LastRecordDT) : "-")}
                        </strong></span>
                    </span>
                </div>
            </div>

            <div className="flex items-center justify-between text-xs px-3 py-1 bg-white border-t border-zinc-200 w-full">
                <div className="flex items-center gap-2">
                    {thread.NextTaskDT || thread.DueDT ? (
                        <span className="text-xs whitespace-nowrap text-zinc-500">
                            Next:{" "}
                            <strong className={`text-xs font-semibold ${getTaskColor(thread.NextTaskDT || thread.DueDT)}`}>
                                {getTableDateOrWeekday(thread.NextTaskDT || thread.DueDT)}
                            </strong>{" "}
                            {thread.NextTaskTyp ? `(${thread.NextTaskTyp})` : ""}
                        </span>
                    ) : (
                        <span className="whitespace-nowrap text-zinc-400 text-xs">
                            Next: Not Set
                        </span>
                    )}
                </div>
                {(thread.ConnID == connection?.ConnID) && isOwnAgencyConnection && (
                    <div className="flex items-center gap-1.5 justify-end">
                        <ActionButton
                            variant="complete"
                            icon={Check}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isPendingTasks(
                                    thread?.ThreadID,
                                    `Cannot complete ${getThreadNameFromTypeID(thread?.TypeID, threadTypesData, "SnSng")}.`,
                                    'Please complete tasks before completing this thread.'
                                )) {
                                    return;
                                }
                                if (dialogStates.isInquiryOnlyMode) {
                                    setDialogStates(prev => ({
                                        ...prev, threadEntry: true, orderEntry: true, isInquiryOnlyMode: true, prefilledData: {
                                            connID: thread?.ConnID,
                                            contactPerson: thread?.connectionData?.contactName || thread?.ContactPerson,
                                            product: thread?.product || thread?.MainProduct,
                                            model: thread?.Model,
                                            reference: thread?.Reference,
                                            quantity: thread?.MainQty,
                                            businessValue: thread?.BizProspect,
                                            notes: thread?.Notes,
                                            source: thread?.Source,
                                            inquiryThreadID: thread?.ThreadID,
                                            ...(thread?.SinceDT && { sinceDT: thread.SinceDT }),
                                        }
                                    }));
                                }
                                else {
                                    handleCompleteThread(thread);
                                }
                            }}
                            loading={threadActionLoading[thread.ThreadID] === 'completing'}
                            tooltip="Complete"
                            disabled={threadActionLoading[thread.ThreadID]}
                        />
                        <ActionButton
                            variant="cancel"
                            icon={X}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isPendingTasks(
                                    thread?.ThreadID,
                                    'Cannot convert inquiry.',
                                    'Please complete tasks before converting this inquiry.'
                                )) {
                                    return;
                                }
                                handleCancelThread(thread);
                            }}
                            loading={threadActionLoading[thread.ThreadID] === 'cancelling'}
                            tooltip="Cancel"
                            disabled={threadActionLoading[thread.ThreadID]}
                        />
                        {canShowEditButton &&
                            <ActionButton
                                variant="edit"
                                icon={Pencil}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditThread(thread.ThreadID, thread.TypeID);
                                }}
                                tooltip="Edit"
                            />
                        }
                    </div>
                )}
            </div>
        </div>
    );

    const OrdersSection = () => {
        const orders = allOrders;

        const handleOrderTypeSelect = () => {
            setDataStates((prev) => ({ ...prev, selectedThreadType: 2 }));
            setDialogStates((prev) => ({ ...prev, threadEntry: true, orderEntry: true }));
        };
        const handleOrderEntry = () => {
            setDataStates((prev) => ({ ...prev, selectedThreadType: 2 }));
            setDialogStates((prev) => ({ ...prev, threadEntry: true, plainOrderEntry: true }));
        };

        return (
            <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
                <CardContent className="p-0">
                    <SectionHeader
                        title={
                            <span className="inline-flex items-center gap-1.5">
                                <span>Recent Sales</span>
                                {orders.length > 0 && (
                                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-[#E2F2E5] text-[#0B5C26]">
                                        {orders.length}
                                    </span>
                                )}
                            </span>
                        }
                        onAdd={orders.length > 0 && isOwnAgencyConnection ? handleOrderEntry : undefined}
                        onHistory={orders.length > 0 && isOwnAgencyConnection ? () => {
                            onClose();
                            navigate(`/threads-report?tID=2&m=${new Date().getMonth() + 1}&cid=${connection?.ConnID}`);
                        } : undefined}
                        historyButton={orders.length > 0 && isOwnAgencyConnection}
                    />

                    {orders.length > 0 ? (
                        <div className="space-y-3 p-3 pt-0">
                            {orders.map((order, index) => (
                                <OrderCard key={order.ThreadID || index} order={order} />
                            ))}
                            {isOwnAgencyConnection && (
                                <Button
                                    className="w-full h-9 py-2 px-4 bg-[#0B5C26] hover:bg-[#08481e] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm transition-colors"
                                    onClick={() => { handleOrderEntry() }}
                                >
                                    <Plus className="h-4 w-4" />
                                    Enter Sale
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="p-3 pt-0">
                            <div className="rounded-lg bg-[#E2F2E5] p-4 flex flex-col">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-md bg-[#C3E8C9] flex items-center justify-center flex-shrink-0 text-[#0B5C26] mt-0.5">
                                        <TrendingUp className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-semibold text-zinc-800">
                                            No sales found
                                        </h4>
                                        <p className="text-xs text-zinc-600 mt-0.5 leading-normal">
                                            Record the first sale once this contact converts
                                        </p>
                                    </div>
                                </div>
                                {isOwnAgencyConnection && (
                                    <Button
                                        className="w-full h-9 mt-3.5 py-2 px-4 bg-[#0B5C26] hover:bg-[#08481e] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm transition-colors"
                                        onClick={() => { handleOrderEntry() }}
                                    >
                                        <Plus className="h-4 w-4" />
                                        Enter Sale
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    };

    const OrderCard = ({ order }) => {
        const isOpenOrder = Number(order.StatusID || order.statusID || order.Sts || 0) !== 2 && Number(order.StatusID || order.statusID || order.Sts || 0) !== 3;
        const stageInfo = stageMap[order.CurrStageID || order.currStageID || order.StatusID];

        let orderStatusText = '';
        if (isOpenOrder) {
            try {
                const rawHist = order.StatusHist || order.statusHist;
                if (rawHist) {
                    const hist = typeof rawHist === 'string' ? JSON.parse(rawHist) : rawHist;
                    if (Array.isArray(hist) && hist.length > 0) {
                        const last = hist[hist.length - 1];
                        orderStatusText = last?.S || last?.Status || '';
                    }
                }
            } catch (e) {
                console.error('Error parsing order StatusHist:', e);
            }
            if (!orderStatusText) {
                orderStatusText = order.CurrStage || order.currStage || order.Status || order.status || stageInfo?.name || "Active";
            }
        } else {
            orderStatusText = Number(order.StatusID) === 3 ? "Cancelled" : "Completed";
        }

        return (
            <div className="bg-zinc-50 rounded-lg border border-zinc-200 shadow-xs w-full flex flex-col justify-between overflow-hidden hover:border-zinc-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => {
                    setSelectedThread(order);
                    if (isOpenOrder) {
                        setDialogStates(prev => ({ ...prev, thrDetails: true }));
                    } else {
                        setDialogStates(prev => ({ ...prev, threadDetail: true, isHistoryview: true }));
                    }
                }}>
                <div className="p-3 flex flex-col gap-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-zinc-800 truncate">
                                {order.MainProduct || order.Product || "N/A"}
                            </h3>
                            {order.Reference && order.Reference !== "0" && (
                                <p className="text-xs text-zinc-500 mt-0.5 truncate font-medium">
                                    Ref: {order.Reference}
                                </p>
                            )}
                        </div>
                        <div className="text-right flex-shrink-0">
                            <span className="text-sm font-semibold text-sahi-green">
                                {order.Amount || order.BizProspect || order.bizProspect ? (
                                    <>
                                        {currencySymbol} {formatAmount((order.Amount || order.BizProspect || order.bizProspect), agencyDB.CountryID)?.toLocaleString("en-US")}
                                    </>
                                ) : (
                                    "--"
                                )}
                            </span>
                            <div className="mt-1">
                                <span className="text-xs text-zinc-500">
                                    {orderStatusText}
                                </span>
                            </div>
                        </div>
                    </div>
                    {order.FdbkScore > 0 && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-zinc-600 bg-zinc-100 p-1.5 rounded">
                            <span className="inline-flex items-center">
                                {sentimentEmoji(order.FdbkScore)}
                            </span>
                            {order.FailureReason && (
                                <span className="font-semibold text-zinc-700">{order.FailureReason} - </span>
                            )}
                            <span className="italic break-words">{order.FdbkNotes || "No comments"}</span>
                        </div>
                    )}
                    {order.Notes && order.Notes !== "--" && (
                        <p className="text-xs text-zinc-500 line-clamp-2 mt-1">
                            {order.Notes}
                        </p>
                    )}
                </div>

                <div className="flex items-center justify-between text-xs px-3 py-1 bg-white border-t border-zinc-200 w-full">
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                        {order.SinceDT ? (
                            <>
                                <Calendar className="h-3 w-3 text-zinc-400" />
                                <span>Recd: <strong className="font-medium text-zinc-700">
                                    {getTableDateOrWeekday(order.SinceDT)}
                                </strong></span>
                            </>
                        ) : (
                            <span className="text-zinc-400">Due: Not Set</span>
                        )}
                    </span>
                    {isOpenOrder && (order.ConnID == connection?.ConnID) && isOwnAgencyConnection ? (
                        <div className="flex items-center gap-1.5 justify-end">
                            <ActionButton
                                variant="complete"
                                icon={Check}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isPendingTasks(
                                        order?.ThreadID,
                                        `Cannot complete ${getThreadNameFromTypeID(order?.TypeID || 2, threadTypesData, "SnSng")}.`,
                                        'Please complete tasks before completing this thread.'
                                    )) {
                                        return;
                                    }
                                    handleCompleteThread(order);
                                }}
                                loading={threadActionLoading[order.ThreadID] === 'completing'}
                                tooltip="Complete"
                                disabled={threadActionLoading[order.ThreadID]}
                            />
                            <ActionButton
                                variant="cancel"
                                icon={X}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isPendingTasks(
                                        order?.ThreadID,
                                        'Cannot cancel sale.',
                                        'Please complete tasks before cancelling this sale.'
                                    )) {
                                        return;
                                    }
                                    handleCancelThread(order);
                                }}
                                loading={threadActionLoading[order.ThreadID] === 'cancelling'}
                                tooltip="Cancel"
                                disabled={threadActionLoading[order.ThreadID]}
                            />
                            {canShowEditButton && (
                                <ActionButton
                                    variant="edit"
                                    icon={Pencil}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEditThread(order.ThreadID, order.TypeID || 2);
                                    }}
                                    tooltip="Edit"
                                />
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-1.5 justify-end">
                            {(order.ThrFile || order.thrFile || order.thrfile) && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 rounded-md transition-colors text-sahi-blue hover:bg-sahi-blue/10 hover:text-sahi-blue"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const filePath = order.ThrFile || order.thrFile || order.thrfile;
                                                setPreviewFilePath(filePath);
                                            }}
                                        >
                                            <Paperclip className="h-3.5 w-3.5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>View Attached Document</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                            {Number(order.StatusID) === 2 && isOwnAgencyConnection && (connection?.Mobile || order?.Mobile) && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 rounded-md transition-colors text-sahi-teal hover:bg-sahi-teal/10 hover:text-sahi-teal"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleFdbkRequest(order);
                                            }}
                                        >
                                            <MessageSquareText className="h-3.5 w-3.5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Send feedback request on WhatsApp</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Helper function to get task type icon
    const getTaskTypeIcon = (type) => {
        const className = "h-3 w-3 mr-1";
        switch (type?.toLowerCase()) {
            case 'visit': return <MapPin className={className} />;
            case 'call': return <Phone className={className} />;
            case 'meeting': return <Users className={className} />;
            case 'email': return <Mail className={className} />;
            case 'log': return <FileText className={className} />;
            case 'dialed': return <PhoneOutgoing className={className} />;
            case 'message': return <MessageSquare className={className} />;
            case 'no response': return <PhoneMissed className="h-3 w-3 mr-1 text-sahi-red" />;
            default: return <ClipboardList className={className} />;
        }
    };

    // Helper function to determine task date range
    const getTaskDateRange = (task) => {
        if (!task?.DT) return null;
        const taskDate = new Date(task.DT);
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const tomorrowStart = new Date(todayStart);
        tomorrowStart.setDate(tomorrowStart.getDate() + 1);
        const weekEnd = new Date(todayStart);
        weekEnd.setDate(weekEnd.getDate() + 7);
        const taskDateStart = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());

        if (taskDateStart < todayStart) return "overdue";
        if (taskDateStart.getTime() === todayStart.getTime()) return "today";
        if (taskDateStart.getTime() === tomorrowStart.getTime()) return "tomorrow";
        if (taskDateStart < weekEnd) return "week";
        return "future";
    };

    // Tasks section component
    const TasksSection = () => (
        <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
            <CardContent className="p-0">
                <SectionHeader
                    title={
                        <span className="inline-flex items-center gap-1.5">
                            <span>Tasks</span>
                            {tasks.length > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-[#E8EDF2] text-[#1E293B]">
                                    {tasks.length}
                                </span>
                            )}
                        </span>
                    }
                    onAdd={tasks.length > 0 ? () => setDialogStates((prev) => ({ ...prev, taskEntry: true })) : undefined}
                    addLabel="Add"
                    onHistory={
                        tasks.length > 0 && isMobile
                            ? () => navigate(`/tasks?cid=${connID || ""}`)
                            : undefined
                    }
                    historyButton={tasks.length > 0 && isMobile}
                />

                {tasks.length > 0 ? (
                    <div className="space-y-3 p-3 pt-0">
                        {sortByDate(tasks, 'DT', 'asc').map((task, index) => (
                            <TaskCard key={task.TskID || index} task={task} />
                        ))}
                        <Button
                            className="w-full h-9 py-2 px-4 bg-[#0F1E36] hover:bg-[#1a2e4c] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm transition-colors"
                            onClick={() => setDialogStates((prev) => ({ ...prev, taskEntry: true }))}
                        >
                            <Plus className="h-4 w-4" />
                            Enter Task
                        </Button>
                    </div>
                ) : (
                    <div className="p-3 pt-0">
                        <div className="rounded-lg bg-[#E8EDF2] p-4 flex flex-col">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-md bg-[#D0DBE5] flex items-center justify-center flex-shrink-0 text-[#1E293B] mt-0.5">
                                    <ListTodo className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-zinc-800">
                                        No tasks found
                                    </h4>
                                    <p className="text-xs text-zinc-600 mt-0.5 leading-normal">
                                        Schedule a follow-up, call or reminder for this contact
                                    </p>
                                </div>
                            </div>
                            <Button
                                className="w-full h-9 mt-3.5 py-2 px-4 bg-[#0F1E36] hover:bg-[#1a2e4c] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm transition-colors"
                                onClick={() => setDialogStates((prev) => ({ ...prev, taskEntry: true }))}
                            >
                                <Plus className="h-4 w-4" />
                                Enter Task
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );

    const TaskCard = ({ task }) => {
        const dateRange = getTaskDateRange(task);
        const isLoading = taskActionLoading[task.TskID];
        const reminderSentPassed = task?.RmdrDT && new Date(task.RmdrDT) < new Date();

        return (
            <div
                className="bg-zinc-50 rounded-lg border border-zinc-200 shadow-xs w-full flex flex-col justify-between overflow-hidden hover:border-zinc-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => {
                    const savedScrollTop = mainContentScrollRef.current?.scrollTop || 0;
                    setSelectedTask(task);
                    setDialogStates(prev => ({ ...prev, taskDetail: true }));
                    // Opening the task detail dialog resets the page's scroll position -
                    // restore it across the next couple of frames so it sticks after the
                    // dialog's own layout/focus shift settles.
                    requestAnimationFrame(() => {
                        if (mainContentScrollRef.current) mainContentScrollRef.current.scrollTop = savedScrollTop;
                        requestAnimationFrame(() => {
                            if (mainContentScrollRef.current) mainContentScrollRef.current.scrollTop = savedScrollTop;
                        });
                    });
                }}
            >
                {/* Body Section */}
                <div className="p-3 flex flex-col gap-1.5">
                    {/* Header Row: Type & Due Date */}
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-xs font-medium text-zinc-500">
                                {task.Typ || "General"}
                            </span>
                            {task.Pri && (() => {
                                const priority = getPriorityDetails(task.Pri);
                                if (!priority.showBadge) return null;
                                const IconComponent = priority.icon;
                                return (
                                    <span style={{ color: priority.color }} title={`${priority.label} Priority`}>
                                        <IconComponent className="h-3.5 w-3.5" strokeWidth={2.5} />
                                    </span>
                                );
                            })()}
                        </div>

                        <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                            {dateRange === "overdue" && <Clock className="h-3.5 w-3.5 text-sahi-red" />}

                            {dateRange === "today" && task.RmdrDT && (
                                reminderSentPassed ? (
                                    <BellRing
                                        className={`h-3.5 w-3.5 ${new Date(task.DT) < new Date()
                                            ? "text-sahi-red"
                                            : "text-sahi-blue"
                                            }`}
                                    />
                                ) : (
                                    <Bell className="h-3.5 w-3.5 text-sahi-blue" />
                                )
                            )}

                            {dateRange !== "overdue" && dateRange !== "today" && (
                                task.RmdrDT ? (
                                    reminderSentPassed ? (
                                        <BellRing className="h-3.5 w-3.5 text-sahi-blue" />
                                    ) : (
                                        <Bell className="h-3.5 w-3.5 text-sahi-blue" />
                                    )
                                ) : (
                                    <Clock className="h-3.5 w-3.5 text-zinc-400" />
                                )
                            )}

                            {dateRange === "today" && !task.RmdrDT && (
                                new Date(task.DT) < new Date() ? (
                                    <BsHourglassBottom className="h-3.5 w-3.5 text-sahi-red" />
                                ) : (
                                    <BsHourglassTop className="h-3.5 w-3.5 text-sahi-blue" />
                                )
                            )}

                            <span className={`font-normal ${dateRange === "overdue"
                                ? "text-sahi-red"
                                : dateRange === "today" && ((task.RmdrDT && reminderSentPassed && new Date(task.DT) < new Date()) || (!task.RmdrDT && new Date(task.DT) < new Date()))
                                    ? "text-sahi-red"
                                    : "text-zinc-600"
                                }`}>
                                {task?.DT ? getRelativeDateText(task.DT, false, true, false, false, true) : "Not set"}
                            </span>
                        </div>
                    </div>

                    {/* Task Title */}
                    <h4 className="text-sm font-semibold text-zinc-800 leading-snug">
                        {task.Ttl || "Untitled Task"}
                    </h4>
                </div>

                {/* Footer Strip */}
                <div className="flex items-center justify-between text-xs px-3 py-1 bg-white border-t border-zinc-200 w-full">
                    <div className="flex items-center gap-1.5 text-xs text-zinc-500 min-w-0">
                        {task.UID && (
                            <>
                                <User className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />
                                <span className="truncate">{getExecutiveName(task.UID)}</span>
                            </>
                        )}
                    </div>
                    <div>
                        {!task?.SCIDs?.split(",").map(Number).includes(connection?.ConnID) && (
                            <div className="flex items-center gap-1.5">
                                {task.POCUserID && task.POCUserID !== currentUser?.UserID && task.CreatedUID === currentUser?.UserID &&
                                    <ActionButton
                                        variant="edit"
                                        icon={BsBellFill}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemindTask(task);
                                        }}
                                        tooltip="Send reminder"
                                        loading={isLoading === 'reminding'}
                                        disabled={isLoading}
                                    />}
                                {(() => {
                                    const myUserIDs = getMyAgenciesMappedIDs('UserID');
                                    const taskCreatorID = Number(task.CreatedUID || 0);
                                    const isCreator = myUserIDs.includes(taskCreatorID) || taskCreatorID === Number(currentUser?.UserID);
                                    const isAssignee = myUserIDs.includes(Number(task.UID)) || Number(task.UID) === Number(currentUser?.UserID);
                                    return task.Sts === 1 && (isCreator || (isAssignee && task.CanComplete == true));
                                })() &&
                                    <ActionButton
                                        variant="complete"
                                        icon={Check}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCompleteTask(task);
                                        }}
                                        tooltip="Mark as complete"
                                        loading={isLoading === 'completing'}
                                        disabled={isLoading}
                                    />}
                                {(() => {
                                    const myUserIDs = getMyAgenciesMappedIDs('UserID');
                                    const taskCreatorID = Number(task.CreatedUID || task.UID || 0);
                                    return myUserIDs.includes(taskCreatorID);
                                })() &&
                                    <ActionButton
                                        variant="cancel"
                                        icon={X}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelTask(task);
                                        }}
                                        tooltip="Cancel task"
                                        loading={isLoading === 'cancelling'}
                                        disabled={isLoading}
                                    />}
                                {(() => {
                                    const myUserIDs = getMyAgenciesMappedIDs('UserID');
                                    const taskCreatorID = Number(task.CreatedUID || task.UID || 0);
                                    const isCreator = myUserIDs.includes(taskCreatorID);
                                    const isAssignee = myUserIDs.includes(Number(task.UID)) || Number(task.UID) === Number(currentUser?.UserID);
                                    return task.Sts === 1 && !isCreator && isAssignee;
                                })() &&
                                    <ActionButton
                                        variant="cancel"
                                        icon={X}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelTask(task);
                                        }}
                                        tooltip="Reject task"
                                        loading={isLoading === 'cancelling'}
                                        disabled={isLoading}
                                    />}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Helper function to parse custom fields
    const parseCustomFields = (customFieldsString) => {
        if (!customFieldsString || customFieldsString.trim() === '') return [];
        try {
            return JSON.parse(customFieldsString);
        } catch (error) {
            console.error("Error parsing custom fields:", error);
            return [];
        }
    };

    // Function to render sections in dynamic order based on data availability
    const renderSectionsInOrder = () => {
        const connTypeStr = (connection?.ConnType || "").toString().toUpperCase();
        const isSupplierOrOther = connTypeStr.includes("S") || connTypeStr.includes("O");

        const sectionConfigs = [
            {
                component: () => <LastInteractionSection key="lastInteraction" />,
                hasData: lastRecord !== null,
                priority: 0
            },
            ...(!isSupplierOrOther ? [
                {
                    component: () => (
                        <div key="threads" ref={threadsRef}>
                            <ThreadsSection />
                        </div>
                    ),
                    hasData: threads.length > 0,
                    priority: 1
                },
                {
                    component: () => (
                        <div key="orders" ref={ordersRef}>
                            <OrdersSection />
                        </div>
                    ),
                    hasData: allOrders.length > 0,
                    priority: 2
                }
            ] : []),
            {
                component: () => (
                    <div key="tasks" ref={tasksRef}>
                        <TasksSection />
                    </div>
                ),
                hasData: tasks.length > 0,
                priority: 3
            },
            {
                component: () => (
                    <div key="contracts" ref={contractsRef}>
                        <ContractsSection />
                    </div>
                ),
                hasData: contracts && contracts.length > 0,
                priority: 4
            },
            {
                component: () => (
                    <div key="transactions" ref={transactionsRef}>
                        <TransactionsSection />
                    </div>
                ),
                hasData: transactions && transactions.length > 0,
                priority: 5
            }
        ];

        // Sort sections: sections with data first (by original priority), then sections without data (by original priority)
        const sortedSections = sectionConfigs.sort((a, b) => {
            if (a.hasData && !b.hasData) return -1; // a has data, b doesn't - a comes first
            if (!a.hasData && b.hasData) return 1;  // b has data, a doesn't - b comes first
            return a.priority - b.priority; // Both have same data status, sort by original priority
        });

        return sortedSections.map(({ component }) => component());
    };

    const AccessCard = () => {
        const alwaysVisibleUsers = resolvedUsers.filter(u =>
            u.isAdmin || u.fullAccess || u.connViewTypeID === 1
        );

        const removableUsers = resolvedUsers.filter(u =>
            u.connViewTypeID === 4 && accessUserIDs.includes(u.userID)
        );

        return (
            <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
                <CardContent className="p-0">
                    <SectionHeader
                        title="Access"
                        customButtons={
                            isOwnAgencyConnection ? <Button
                                variant="ghost"
                                size="md"
                                onClick={() => {
                                    const eligibleUsers = resolvedUsers.filter(u => u.connViewTypeID === 4 && !u.isAdmin && !u.fullAccess && u.userID !== connection?.POCUserID);
                                    const availableUsers = eligibleUsers.filter(u => !accessUserIDs.includes(u.userID));

                                    if (availableUsers.length === 0) {
                                        setDialogStates((prev) => ({ ...prev, allUsersHaveAccessAlert: true }));
                                    } else {
                                        setDialogStates((prev) => ({ ...prev, accessDialog: true }));
                                    }
                                }}
                                className={`hover:bg-zinc-100 rounded-md`}
                            >
                                <Plus className="h-5 w-5" />
                            </Button> : null
                        }
                    />
                    <div className="p-3 pt-0">
                        <div className="flex flex-wrap gap-2">
                            {alwaysVisibleUsers.map(user => (
                                <span
                                    key={`static-${user.userID}`}
                                    className="inline-flex items-center rounded border px-2.5 py-0.5 text-xs font-semibold border-none bg-zinc-100 text-foreground h-7"
                                >
                                    {user.name}
                                </span>
                            ))}

                            {removableUsers.map(user => (
                                <span
                                    key={`removable-${user.userID}`}
                                    className="inline-flex items-center rounded border px-2.5 py-0.5 text-xs font-semibold border-none bg-zinc-100 text-foreground h-7"
                                >
                                    {user.name}
                                    {isOwnAgencyConnection && <X
                                        className="ml-2 h-3 w-3 cursor-pointer text-zinc-500 hover:text-red-500"
                                        onClick={() => setRemoveAccessState({ open: true, userID: user.userID })}
                                    />}
                                </span>
                            ))}

                            {alwaysVisibleUsers.length === 0 && removableUsers.length === 0 && (
                                <div className="text-sm text-sub-text-color italic">No access defined. Click + to add users.</div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    // Information/Details section component
    const InformationSection = () => {
        const strLocation = [connection?.City, connection?.State, Number(connection?.CountryID) === Number(agencyData?.CountryID) ? null : connection?.Country].filter(Boolean).join(", ");
        const customFields = parseCustomFields(connection?.CustomFields);
        const hasNotes = connection?.Notes && connection?.Notes.trim() !== '';
        const currentRegion = regions.find(rgn => rgn.RID === connection?.RegionID)?.Rgn;

        const hasDetails = connection?.SinceDT || connection?.Source || connection?.Segment || strLocation || connection?.Potential > 0 ||
            connection?.TaxID || hasNotes || (connection?.Tags && connection?.Tags.trim() !== '') || customFields.length > 0 || connection?.RegionID || connection?.ConnType === 'C';

        if (!hasDetails) return null;

        const getBadgeData = () => {
            const badges = [];
            const addToBadgesList = (label, value, className = `${styleClasses.textColor}`, nested = null, onClick = null, showTooltip = false, tooltipMessage = null, emoji = null, icon = null, suffix = null, iconColor = null, dotColor = null) => {
                badges.push({ label, value, className, nested, onClick, showTooltip, tooltipMessage, emoji, icon, suffix, iconColor, dotColor });
            };

            if (connection?.SinceDT) {
                let srcData = null;
                if (connection?.Source) {
                    srcData = { label: ' from', value: connection?.Source }
                }
                addToBadgesList("Since", getDateWithoutTime(connection?.SinceDT), styleClasses.textColor, srcData);
            }

            if (connection?.ConnType.includes('C')) {
                // Decide if we should show tooltip or clickable badge
                const shouldShowTooltip = isFeedbackSentToday;

                // Tooltip message
                let tooltipMsg = "";
                if (isFeedbackSentToday) {
                    tooltipMsg = "You've already sent a sentiment request today.";
                } else if (lastFeedbackDT) {
                    tooltipMsg = `Last Requested on ${format(lastFeedbackDT, "dd-MMM-yy")}`;
                } else {
                    tooltipMsg = "Click to send a sentiment request.";
                }

                addToBadgesList(
                    "Sentiment:",
                    getFdbkScoreText(connection?.FdbkScore),
                    undefined,
                    null,
                    shouldShowTooltip
                        ? null
                        : (canDeleteConnections && canCreateConnections && canEditConnections === 1
                            ? () => setDialogStates((prev) => ({ ...prev, fdbkRequest: true }))
                            : null),
                    shouldShowTooltip,
                    tooltipMsg,
                    sentimentEmoji(connection?.FdbkScore)
                );
            }

            if (connection?.Segment) {
                const segmentData = masterData?.segments?.find((s) => s.CNm === connection.Segment);
                const segmentColor = segmentData?.Clr ? `#${segmentData.Clr}` : null;
                addToBadgesList(null, connection?.Segment, undefined, null, null, false, null, null, null, null, null, segmentColor);
            }

            if (strLocation) {
                addToBadgesList(null, strLocation, undefined, null, null, false, null, null, MapPin, null, "text-sahi-dark-blue");
            }

            if (currentRegion) {
                addToBadgesList("Region:", currentRegion);
            }

            if (connection?.Potential > 0) {
                addToBadgesList(null, `${currencySymbol} ${connection?.Potential.toLocaleString("en-US")}`, undefined, null, null, false, null, null, FaSackDollar, "/yr", "text-sahi-green");
            }

            if (connection?.TaxID) {
                addToBadgesList(`${taxIDLabel}:`, connection?.TaxID);
            }

            // Add custom fields
            customFields.forEach((field) => {
                addToBadgesList(`${field.F}:`, field.V);
            });

            if (hasNotes) {
                addToBadgesList("Notes:", connection?.Notes);
            }

            return badges;
        };

        return (
            <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
                <CardContent className="p-0">
                    <SectionHeader
                        title="Information"
                        customButtons={
                            isOwnAgencyConnection ? (
                                <div
                                    onClick={() => { setDialogStates(prev => ({ ...prev, editDialog: true })) }}
                                    className="cursor-pointer"
                                >
                                    <Pencil className="h-4 w-4" />
                                </div>
                            ) : null
                        }
                    />

                    <div className="space-y-3">
                        <div className="flex flex-wrap gap-2 items-start p-3 pt-0">
                            {getBadgeData().map((badge, index) => {
                                const badgeContent = (
                                    <div
                                        className={`inline-flex items-start gap-1.5 bg-zinc-100 px-3 py-2 rounded text-sm text-foreground break-words min-h-[40px] h-auto ${badge.onClick ? "cursor-pointer hover:bg-zinc-200" : ""
                                            }`}
                                        onClick={badge.onClick || undefined}
                                    >
                                        {badge.icon && <badge.icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${badge.iconColor || ''}`} />}
                                        {badge.dotColor && (
                                            <div
                                                className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5"
                                                style={{ backgroundColor: badge.dotColor }}
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            {badge.label && <span>{badge.label} </span>}
                                            <span className={`font-semibold ${badge.className}`}>
                                                {badge.value}
                                                {badge.emoji && (
                                                    <span className="ml-1 inline-block">
                                                        {badge.emoji}
                                                    </span>
                                                )}
                                            </span>
                                            {badge.suffix && <span>{badge.suffix}</span>}
                                            {badge.nested && (
                                                <>
                                                    <span>{badge.nested?.label} </span>
                                                    <span className={`font-semibold ${badge.className}`}>
                                                        {badge.nested?.value}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );

                                // If badge should show tooltip, wrap with CustomTooltip
                                if (badge.showTooltip) {
                                    return (
                                        <CustomTooltip
                                            key={index}
                                            content={(data) => (
                                                <div className="p-2 bg-sahi-primary-color rounded-md">
                                                    <p className="italic text-sahi-white text-sm">{data}</p>
                                                </div>
                                            )}
                                        >
                                            {({ onMouseEnter, onMouseLeave }) => (
                                                <div
                                                    className="inline-block bg-zinc-100 px-3 py-2 rounded text-sm text-foreground break-words line-clamp-2"
                                                    onMouseEnter={(e) => onMouseEnter(badge.tooltipMessage, e)}
                                                    onMouseLeave={onMouseLeave}
                                                    onClick={badge.onClick || undefined}
                                                >
                                                    <span>{badge.label} </span>
                                                    <span className={`font-semibold ${badge.className}`}>
                                                        {badge.value}
                                                        {badge.emoji && (
                                                            <span className="ml-1 inline-block">
                                                                {badge.emoji}
                                                            </span>
                                                        )}</span>
                                                    {badge.nested && (
                                                        <>
                                                            <span>{badge.nested?.label} </span>
                                                            <span className={`font-semibold ${badge.className}`}>
                                                                {badge.nested?.value}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </CustomTooltip>
                                    );
                                }

                                // Otherwise return badge as is with key
                                return <div key={index}>{badgeContent}</div>;
                            })}
                        </div>

                        {connection?.Tags && connection?.Tags.trim() !== '' && (
                            <div className="flex flex-wrap gap-1.5 px-3 pb-3 pt-2.5 border-t border-zinc-100 !mt-0">
                                {connection?.Tags.split(',').map((tag, index) => {
                                    const trimmedTag = tag.trim();
                                    if (!trimmedTag) return null;

                                    return (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-zinc-100 rounded text-foreground"
                                        >
                                            <span className="text-zinc-400 font-normal mr-0.5">#</span>
                                            {trimmedTag}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    };

    // Business card section component
    const BusinessCardSection = () => {
        const businessCardPath = connection?.ContactCard || connection?.Attachment || connection?.attachment;
        if (!businessCardPath) return null;

        const handleEditBusinessCard = () => {
            setDialogStates((prev) => ({ ...prev, editDialog: true }));
            // Wait for the edit dialog to mount before scrolling to its business card section.
            let attempts = 0;
            const tryScroll = () => {
                const el = document.getElementById('business-card-section');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (attempts < 10) {
                    attempts += 1;
                    setTimeout(tryScroll, 150);
                }
            };
            setTimeout(tryScroll, 150);
        };

        const handleImageLoad = (e) => {
            const { naturalWidth, naturalHeight } = e.target;
            if (naturalWidth && naturalHeight) {
                setBusinessCardRatio(naturalWidth / naturalHeight);
            }
        };

        return (
            <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
                <CardContent className="p-0">
                    <div className={`${styleClasses.sectionHeader} flex items-center justify-between`}>
                        <h3 className={styleClasses.sectionTitle}>Business Card</h3>
                        {isOwnAgencyConnection && (canEditConnections === 1 || canEditConnections == 2) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleEditBusinessCard}
                                className="h-7 w-7 p-0 flex-shrink-0"
                                title="Edit Business Card"
                            >
                                <Pencil className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                    <div className="p-3 pt-0">
                        {businessCardRatio === null ? (
                            <div className="relative group w-full bg-zinc-50 overflow-hidden h-[150px] flex items-center justify-center animate-pulse">
                                <span className="text-xs text-zinc-400">Loading business card...</span>
                                <img
                                    src={getUploadUrl(`Uploads/${businessCardPath}`)}
                                    alt="Business Card"
                                    className="absolute opacity-0 w-0 h-0"
                                    onLoad={handleImageLoad}
                                />
                            </div>
                        ) : (
                            <div className="relative group w-full bg-white overflow-hidden rounded-lg border border-zinc-200">
                                <img
                                    src={getUploadUrl(`Uploads/${businessCardPath}`)}
                                    alt="Business Card"
                                    className="w-full h-auto block cursor-zoom-in rounded-lg"
                                    onClick={() => {
                                        setFullPreviewUrl(getUploadUrl(`Uploads/${businessCardPath}`));
                                        setIsFullPreviewLoading(true);
                                        setIsFullPreviewOpen(true);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    };

    // Transactions section component
    const TransactionsSection = () => {
        if (!showModule.transactions) return null;
        return (
            <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
                <CardContent className="p-0">
                    <SectionHeader
                        title={transactions.length > 0 ? `Transactions (${transactions.length})` : 'Transactions'}
                        customButtons={
                            isOwnAgencyConnection ? (
                                <TransactionDropdown
                                    onTransactionTypeSelect={(typeId) => {
                                        setSelectedTransactionType(typeId);
                                        setSelectedTransaction(null);
                                        setTransactionPrefilledData({});
                                        setDialogStates(prev => ({ ...prev, transactionEntry: true }));
                                    }}
                                    connectionType={connection?.ConnType}
                                    externalOpen={transactionDropdownOpen}
                                    onExternalOpenChange={setTransactionDropdownOpen}
                                    className="!h-5 !w-5"
                                    title="Add"
                                    minimalIcon={true}
                                />
                            ) : null
                        }
                    />

                    {transactions.length > 0 ? (
                        <div className="space-y-3 mt-3 p-3 pt-0">
                            {transactions.map((transaction, index) => (
                                <TransactionCard key={transaction.XnID || index} transaction={transaction} />
                            ))}
                        </div>
                    ) : (
                        <NoDataCard
                            staticIcon={currencySymbol}
                            title="No transactions found."
                            desc="Click to add a transaction."
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isOwnAgencyConnection) setTransactionDropdownOpen(true);
                            }}
                        />
                    )}
                </CardContent>
            </Card>
        );
    };

    // Contracts section component
    const ContractsSection = () => {
        if (!showModule.contracts) return null;

        return (
            <Card className={`${styleClasses.sectionCard} ${styleClasses.cardSpacing}`}>
                <CardContent className="p-0">
                    <SectionHeader
                        title={contracts.length > 0 ? `Contracts (${contracts.length})` : 'Contracts'}
                        customButtons={
                            isOwnAgencyConnection ? (
                                <ContractDropdown
                                    onContractTypeSelect={(contractType) => {
                                        setSelectedContractType(contractType);
                                        setSelectedContract(null);
                                        setContractPrefilledData({});
                                        setDialogStates(prev => ({ ...prev, contractEntry: true }));
                                    }}
                                    connectionType={connection?.ConnType}
                                    externalOpen={contractDropdownOpen}
                                    onExternalOpenChange={setContractDropdownOpen}
                                    className="!h-5 !w-5"
                                    title="Add"
                                    minimalIcon={true}
                                />
                            ) : null
                        }
                    />

                    {contracts.length > 0 ? (
                        <div className="space-y-3 mt-3 p-3 pt-0">
                            {contracts.map((contract, index) => (
                                <ContractCard key={contract.ContractID || index} contract={contract} />
                            ))}
                        </div>
                    ) : (
                        <NoDataCard
                            Icon={FileText}
                            title="No contracts found."
                            desc="Click to add a contract."
                            onClick={() => {
                                if (!canAddCtrc) return;
                                // Handle click based on connection type, same as ContractDropdown logic
                                if (connection?.ConnType === 'C') {
                                    // Customer connection - directly create sold contract
                                    setSelectedContractType('sold');
                                    setSelectedContract(null);
                                    setContractPrefilledData({});
                                    setDialogStates((prev) => ({ ...prev, contractEntry: true }));
                                } else if (connection?.ConnType === 'S') {
                                    // Supplier connection - directly create purchase contract
                                    setSelectedContractType('purchase');
                                    setSelectedContract(null);
                                    setContractPrefilledData({});
                                    setDialogStates((prev) => ({ ...prev, contractEntry: true }));
                                } else {
                                    // No specific connection type or mixed - show dropdown
                                    setContractDropdownOpen(true);
                                }
                            }}
                        />
                    )}
                </CardContent>
            </Card>
        );
    };
    const ContractCard = ({ contract }) => {
        const isExpired = contract.ExpireDate && new Date(contract.ExpireDate) < new Date();

        // Handle contract card click to open details dialog
        const handleContractClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Open contract details dialog
            setSelectedContractForDetail(contract);
            setDialogStates(prev => ({ ...prev, contractDetail: true }));
        };

        return (
            <div className={styleClasses.mobileCard}>
                <div
                    className={`${styleClasses.mobileHeader} !bg-sahi-disable cursor-pointer`}
                    onClick={handleContractClick}
                >
                    <div className="flex flex-row justify-between gap-3">
                        {/* Left side - Product and Contract details */}
                        <div className="flex flex-col flex-1 min-w-0">
                            {/* Product name */}
                            <div className={`text-sm font-semibold ${styleClasses.textColor} mb-2 flex items-center`}>
                                <span className="line-clamp-1">
                                    {contract.Product || "-"}
                                </span>
                            </div>

                            {/* Contract Amount */}
                            {contract.ContractCharges != 0 ? (
                                <div className={`text-sm font-medium ${styleClasses.textColor} mb-2 flex items-center`}>
                                    {getCurrencySymbol(contract.CurrencyID)} {formatAmount(contract.ContractCharges, agencyDB.CountryID)?.toLocaleString("en-US") || "0"}
                                </div>
                            ) : "-"}

                            {/* Contract number with icon - aligned horizontally */}
                            <div className={`${styleClasses.bodyText} flex items-center gap-1`}>
                                <div className="text-sahi-purple flex items-center">
                                    {contract.IsSeller == 1 ? <FileText className="h-4 w-4 text-sahi-green" /> : <FileText className="h-4 w-4 text-sahi-orange" />}
                                </div>
                                <span className={`${styleClasses.textColor} font-medium`}>
                                    #{contract.ContractNo}
                                </span>
                            </div>
                        </div>

                        {/* Right side - Type and other details */}
                        <div className="text-right flex-shrink-0 flex flex-col">
                            {/* Contract Type with expand indicator */}
                            <div className={`text-sm font-medium ${styleClasses.textColor} mb-2 flex items-center justify-end gap-1`}>
                                <span>{contract.ContractType || "Contract Type"}</span>
                            </div>

                            {/* Renewal Amount */}
                            {contract.RenewalCharges != 0 ? (
                                <div className={`${styleClasses.bodyText} mb-2 flex items-center justify-end`}>
                                    Renew: &nbsp;{getCurrencySymbol(contract.CurrencyID)} {formatAmount(contract.RenewalCharges, agencyDB.CountryID)?.toLocaleString("en-US") || "0"}
                                </div>
                            ) : "-"}

                            {/* Start date - aligned horizontally with contract number */}
                            <div className={`${styleClasses.bodyText} flex items-center justify-end`}>
                                <span>Start: {contract.StartDT ? getDateWithoutTime(contract.StartDT) : '--'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`${styleClasses.mobileFooter} !bg-background`}>
                    <div className="flex items-center">
                        {/* Expiry date */}
                        <span className={`text-xs ${isExpired ? 'text-sahi-red font-medium' : styleClasses.textSubColor}`}>
                            {isExpired && "Expired: "}
                            {contract.ExpiryDT ? getDateWithoutTime(contract.ExpiryDT) : 'No expiry date'}
                        </span>
                    </div>

                    <div className="flex gap-2 items-center">
                        {/* Download button */}
                        {contract.Attachment && (
                            <ActionButton
                                variant="edit"
                                icon={Download}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (contract.Attachment) {
                                        const fileUrl = `/Uploads/${contract.Attachment}`;
                                        navigate(fileUrl);
                                    }
                                }}
                                tooltip="Download Contract"
                            />
                        )}

                        {/* Contact Actions */}
                        <ContactActions
                            connectionData={{
                                connID: connection?.ConnID || "",
                                contactName: connection?.ContactName || "",
                                bizName: connection?.BizName || "",
                                mobile: contract.ContactMobile || connection?.Mobile || "",
                                email: contract.ContactEmail || connection?.Email || "",
                                cc: connection?.CC || "91",
                            }}
                            connID={connection?.ConnID}
                            showEmail={false}
                            showTemplate={connection?.ConnType.includes('C') ? true : false}
                        />
                        {canEditContracts &&
                            <ActionButton
                                variant="edit"
                                icon={Pencil}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    // Handle contract edit - UPDATED CODE BELOW
                                    const startDate = contract.StartDT ? new Date(contract.StartDT) : null;
                                    const expireDate = contract.ExpiryDT ? new Date(contract.ExpiryDT) : null;
                                    const contractType = contract.IsSeller ? 'sold' : 'purchase';

                                    const prefilledData = {
                                        connection: contract.ConnID,
                                        contactPerson: contract.ContactName,
                                        contractNo: contract.ContractNo,
                                        contractType: contract.ContractType,
                                        contractTypeSelection: contractType, // This is important for the dropdown
                                        product: contract.Product,
                                        startDate: startDate ? format(startDate, "yyyy-MM-dd") : "",
                                        expireDate: expireDate ? format(expireDate, "yyyy-MM-dd") : "",
                                        charges: contract.ContractCharges,
                                        renewal: contract.RenewalCharges,
                                        notes: contract.Notes,
                                        bizName: connection?.BizName || "",
                                        contactName: contract.ContactName,
                                        mobile: contract.ContactMobile || connection?.Mobile || "",
                                        email: contract.ContactEmail || connection?.Email || "",
                                        fileName: contract.Attachment || ""
                                    };

                                    setSelectedContractType(contractType);
                                    setSelectedContract(contract);
                                    setContractPrefilledData(prefilledData);
                                    setDialogStates(prev => ({ ...prev, contractEntry: true }));
                                }}
                                tooltip="Edit Contract"
                            />}
                    </div>
                </div>
            </div>
        );
    };


    // Transaction card component
    const TransactionCard = ({ transaction }) => {
        const transactionType = getTransactionTypeById(transaction.XnTypeID);
        const contactName = transaction.IsSeller ? transaction.BuyerUserName : transaction.SellerUserName;

        return (
            <div className={styleClasses.mobileCard}>
                <div className={`${styleClasses.mobileHeader} !bg-sahi-disable`}>
                    <div className="flex flex-row justify-between gap-2">
                        <div className="flex flex-col items-start justify-between">
                            <div className="flex items-center gap-2 mb-2">
                                <div className={getTransactionIconColorClass(transactionType)}>
                                    {getTransactionIcon(transactionType)}
                                </div>
                                <span className={`text-sm font-semibold ${styleClasses.textColor}`}>
                                    {transactionType?.LblF}
                                </span>
                            </div>
                            {contactName && (
                                <div className={styleClasses.bodyText}>
                                    Contact: <span className={`${styleClasses.textColor} font-medium`}>{contactName}</span>
                                </div>
                            )}
                        </div>
                        <div className="text-right flex-shrink-0">
                            <div className={`text-sm font-medium ${styleClasses.textColor} mb-1`}>
                                {getCurrencySymbol(transaction.CurrencyID)} {formatAmount(transaction.AmtGrandTotal, agencyDB.CountryID)?.toLocaleString("en-US") || "0"}
                            </div>
                            <div className={styleClasses.bodyText}>
                                {transaction.XnDate ? getDateWithoutTime(transaction.XnDate) : '--'}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row justify-between">
                        <div className={styleClasses.bodyText}>
                            No: <span className={`${styleClasses.textColor} font-medium`}>{transaction.XnNo}</span>
                        </div>
                    </div>
                </div>

                <div className={`${styleClasses.mobileFooter} !bg-background`}>
                    <div className="flex flex-wrap gap-3 items-center">
                        {/* Additional transaction info can go here */}
                    </div>
                    <div className="flex gap-2 text-lg justify-end items-center">
                        {transaction.XnFile && (
                            <ActionButton
                                variant="edit"
                                icon={Paperclip}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (transaction.XnFile) {
                                        const fileUrl = `/Uploads/${transaction.XnFile}`;
                                        navigate(fileUrl);
                                    }
                                }}
                                tooltip="Download PDF"
                            />
                        )}

                        <ContactActions
                            connectionData={{
                                connID: connection?.ConnID || "",
                                contactName: connection?.ContactName || "",
                                bizName: connection?.BizName || "",
                                mobile: transaction.IsSeller ? transaction.BuyerMobile : transaction.SellerMobile,
                                email: transaction.IsSeller ? transaction.BuyerEmail : transaction.SellerEmail,
                                cc: transaction.IsSeller ? transaction.BuyerCC : transaction.SellerCC
                            }}
                            connID={connection?.ConnID}
                        />

                        {canEditXn &&
                            <ActionButton
                                variant="edit"
                                icon={Pencil}
                                onClick={() => {
                                    const invoiceDate = transaction.XnDate ? new Date(transaction.XnDate) : null;
                                    const dueDate = transaction.DueDate ? new Date(transaction.DueDate) : null;

                                    const prefilledData = {
                                        connection: transaction.ConnID,
                                        contactPerson: transaction.IsSeller ? transaction.BuyerUserName : transaction.SellerUserName,
                                        invoiceNo: transaction.XnNo,
                                        referenceNo: transaction.ConnRefNo,
                                        xnDate: invoiceDate ? format(invoiceDate, "yyyy-MM-dd") : "",
                                        dueDate: dueDate ? format(dueDate, "yyyy-MM-dd") : "",
                                        notes: transaction.IntNotes,
                                        bizName: transaction.IsSeller ? transaction.BuyerAgencyName : transaction.SellerAgencyName,
                                        contactName: transaction.IsSeller ? transaction.BuyerUserName : transaction.SellerUserName,
                                        fileName: transaction.XnFile || "",
                                        preTaxAmount: transaction.AmtSubTotal,
                                        taxAmount: transaction.AmtTaxTotal,
                                        totalAmount: transaction.AmtGrandTotal
                                    };

                                    setSelectedTransactionType(transaction.XnTypeID);
                                    setSelectedTransaction(transaction);
                                    setTransactionPrefilledData(prefilledData);
                                    setDialogStates(prev => ({ ...prev, transactionEntry: true }));
                                }}
                                tooltip="Edit"
                            />
                        }


                    </div>
                </div>
            </div>
        );
    };

    // Stable callback for thread updates to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleThreadUpdated = useCallback(async () => {
        await refreshConnectionData();
    }, [connID, connection?.ConnID]);

    // Stable initial data object to prevent loops
    const threadInitialData = useMemo(() => {
        if (!selectedThread || !(dialogStates.isHistoryThread || dialogStates.isHistoryview)) {
            return null;
        }
        return {
            ...selectedThread,
            connectionData: {
                BizName: connection?.BizName,
                ContactName: connection?.ContactName,
                CC: connection?.CC,
                Mobile: connection?.Mobile,
                Email: connection?.Email,
                ConnType: connection?.ConnType
            }
        };
    }, [selectedThread, dialogStates.isHistoryThread, dialogStates.isHistoryview, connection?.BizName, connection?.ContactName, connection?.CC, connection?.Mobile, connection?.Email, connection?.ConnType]);

    return (
        <TooltipProvider>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent
                    className="w-full h-full max-w-full max-h-full p-0 overflow-hidden flex flex-col gap-0 sm:max-w-4xl sm:max-h-[90vh] sm:rounded-lg"
                >
                    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                        {/* Mobile Header */}
                        <div className="sm:hidden flex items-center justify-between w-full h-8">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className="h-[24px] w-[24px]" />
                                    <span className="sr-only">Back</span>
                                </DialogClose>
                                <DialogTitle className="font-medium flex items-center gap-2">
                                    <div className={styleClasses.truncate}>
                                        Contact Details
                                    </div>
                                </DialogTitle>
                            </div>
                            {isOwnAgencyConnection && <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDialogStates((prev) => ({ ...prev, editDialog: true }))}
                                className=" p-2"
                            >
                                <Pencil className="h-4 w-4" />
                            </Button>}
                        </div>

                        {/* Desktop Header */}
                        <div className="hidden sm:flex items-center justify-between w-full">
                            <div className="flex items-center gap-3">
                                <div>
                                    <DialogTitle className={`text-xl ${styleClasses.textColor} flex items-center gap-2`}>
                                        {`${connection?.ConnType === 'C' ? 'Customer' :
                                            connection?.ConnType === 'S' ? 'Supplier' :
                                                connection?.ConnType === 'O' ? 'Other' :
                                                    connection?.ConnType?.includes('C') && connection?.ConnType?.includes('S') ? 'Contact Details' :
                                                        ''} Details - ${connection?.BizName || connection?.ContactName || "Company Name"}`.replace(/^[ -]+/, '')}
                                        {/* {isOwnAgencyConnection && <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleToggleColdContact}
                                            className="h-6 w-6 p-0 hover:bg-transparent"
                                        >
                                            <GiIceCube
                                                className={`h-5 w-5 flex-shrink-0 cursor-pointer transition-colors ${connection?.IsCold === 1 || connection?.IsCold === true
                                                    ? 'text-blue-500'
                                                    : 'text-zinc-800'
                                                    }`}
                                            />
                                        </Button>} */}
                                    </DialogTitle>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {isOwnAgencyConnection && <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDialogStates((prev) => ({ ...prev, editDialog: true }))}
                                    className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-zinc-200"
                                >
                                    <Pencil className="h-4 w-4" />
                                </Button>}
                                <DialogClose
                                    onClick={onClose}
                                    className="h-9 w-9 p-0 rounded-md hover:bg-bg-grey flex items-center justify-center"
                                >
                                    <X className={`h-4 w-4 ${styleClasses.textColor}`} />
                                </DialogClose>
                            </div>
                        </div>
                    </DialogHeader>

                    {/* Main Content */}
                    <div ref={mainContentScrollRef} className="flex-1 overflow-y-auto bg-var(--background)">
                        <CompanyInfoSection
                            onScrollToSection={scrollToSection}
                            refs={{ threadsRef, ordersRef, tasksRef, transactionsRef, contractsRef, contactsRef }}
                        />
                        <FeedbackSection
                            feedbackScore={connection?.FdbkScore || 0}
                            feedbackNotes={connection?.FdbkNotes || ""}
                            failureReason={feedbackThreadDetail?.thrFailureRsn || ""}
                            isConnection={true}
                            threadDetail={feedbackThreadDetail}
                        />

                        {/* Render sections in dynamic order based on data availability */}
                        {renderSectionsInOrder()}

                        <div ref={contactsRef}>
                            {connection?.ContactName && contacts && contacts.length > 0 && <ContactsSection />}
                        </div>
                        <InformationSection />
                        <BusinessCardSection />
                        {(currentUser?.IsAdmin || currentUser?.FullAccess) && <AccessCard />}
                        <div className="h-6"></div>
                    </div>
                </DialogContent>

                {/* All Dialog Components - Keeping original structure for functionality */}
                {dialogStates.threadEntry && (
                    <ThreadEntry
                        triggerButton={false}
                        threadTypeID={
                            dialogStates.isInquiryOnlyMode
                                ? (dialogStates.orderEntry ? 2 : (dialogStates.plainOrderEntry ? 2 : (dialogStates.ordHistoryEdit ? 2 : 1)))
                                : dataStates.selectedThreadType
                        }
                        threadID={editingThreadID}
                        open={dialogStates.threadEntry}
                        onOpenChange={async (open) => {
                            setDialogStates((prev) => ({
                                ...prev,
                                threadEntry: open,
                                orderEntry: open ? prev.orderEntry : false,
                                plainOrderEntry: open ? prev.plainOrderEntry : false,
                                ordHistoryEdit: open ? prev.ordHistoryEdit : false
                            }));
                            if (!open) {
                                setEditingThreadID(null);
                                setEditingThreadTypeID(null);
                                await refreshConnectionData();
                            }
                        }}
                        preselectedConnID={connection?.ConnID || connection?.id}
                        preselectedContactPerson={connection?.ContactName || ""}
                        fromConnectionDetail={true}
                        calledFrom={!isInqOnlyMode ? '' : (dialogStates.orderEntry ? 'INQDONE' : (dialogStates.plainOrderEntry ? '' : ''))}
                        allowOpenOrders={dialogStates.plainOrderEntry}
                        prefilledData={dialogStates.prefilledData}
                        extOperation={dialogStates.ordHistoryEdit ? 'EDIT' : ''}
                    />
                )}

                <WspDialog
                    isOpen={isWspDialogOpen}
                    onClose={() => setIsWspDialogOpen(false)}
                    connectionData={{
                        ...connection,
                        cc: connection?.CC,
                        connID: connection?.ConnID || "",
                        contactName: connection?.ContactName || "",
                        bizName: connection?.BizName || "",
                    }}
                    phone={connection?.Mobile || ''}
                    type="contact"
                />

                {dialogStates.taskEntry && (
                    <TaskEntry
                        key={editingTaskID || 'new-task'}
                        dialogTitle={editingTaskID ? 'Edit Task' : 'Add Task'}
                        triggerButton={false}
                        open={dialogStates.taskEntry}
                        onOpenChange={(open) => {
                            setDialogStates((prev) => ({ ...prev, taskEntry: open }));
                            if (!open) {
                                setEditingTaskID(null);
                                setTaskEntryMode('normal');
                                setRepeatPrefillData(null);
                            }
                        }}
                        prefilledData={taskEntryMode === 'repeat' ? repeatPrefillData : editingTaskID ? (() => {
                            const tasks = getTasksData();
                            const task = tasks.find(t => t.TskID === editingTaskID);
                            return task ? {
                                ...formatTaskData(task),
                                connID: connection?.ConnID,
                                connectionLocked: true,
                                connectionLabel: 'Contact',
                                bizName: connection?.BizName || '',
                                contactName: connection?.ContactName || '',
                                threads: task.TIDs ? task.TIDs.split(',') : '',
                                secConnIDs: task.SCIDs ? task.SCIDs.split(",").map(id => Number(id)).filter(Boolean) : ''
                            } : {
                                connID: connection?.ConnID,
                                connectionLocked: true,
                                connectionLabel: 'Contact',
                                bizName: connection?.BizName || '',
                                contactName: connection?.ContactName || '',
                                threads: '',
                                secConnIDs: ''
                            };
                        })() : (() => {
                            return {
                                connID: connection?.ConnID,
                                connectionLocked: true,
                                connectionLabel: 'Contact',
                                bizName: connection?.BizName || '',
                                contactName: connection?.ContactName || '',
                                mobile: connection?.Mobile || '',
                                email: connection?.Email || '',
                                threads: '',
                                connectionType: connection?.ConnType || '',
                                source: connection?.Source || '',
                                segment: connection?.Segment || '',
                            };
                        })()}
                    />
                )}

                {dialogStates.contractEntry && (
                    <ContractEntry
                        isOpen={dialogStates.contractEntry}
                        onClose={() => {
                            setDialogStates(prev => ({ ...prev, contractEntry: false }));
                            setSelectedContract(null);
                            setContractPrefilledData({});
                        }}
                        connID={connID}
                        companyName={connection?.BizName || ""}
                        prefilledData={{
                            ...contractPrefilledData,
                            contractTypeSelection: selectedContractType // Make sure this is passed
                        }}
                        contractID={selectedContract?.ContractID || null}
                        onContractSaved={refreshConnectionData}
                    />
                )}
                <RecordEntry
                    triggerButton={false}
                    open={dialogStates.recordEntry}
                    onOpenChange={(open) => {
                        setDialogStates((prev) => ({ ...prev, recordEntry: open }));
                        if (!open) {
                            setSelectedTaskForRecord(null);
                        }
                    }}
                    prefilledData={{
                        connID: connection?.ConnID,
                        connectionLocked: true,
                        bizName: connection?.BizName || '',
                        contactName: connection?.ContactName || '',
                        mobile: connection?.Mobile || '',
                        email: connection?.Email || '',
                        connectionType: connection?.ConnType || '',
                        threadIDs: selectedTaskForRecord ? (selectedTaskForRecord.TIDs ? selectedTaskForRecord.TIDs.split(",") : []) : [],
                    }}
                    selectedTaskForRecord={selectedTaskForRecord ? formatTaskData(selectedTaskForRecord) : null}
                    onRecordAdded={async () => {
                        await refreshConnectionData();
                        if (pendingRepeatAfterRecordRef.current) {
                            pendingRepeatAfterRecordRef.current = false;
                            openRepeatTaskEntry(selectedTaskForRecord);
                        }
                    }}
                    showUpdateStatus={true}
                />

                {dialogStates.transactionEntry && (
                    <TransactionEntry
                        isOpen={dialogStates.transactionEntry}
                        onClose={() => {
                            setDialogStates(prev => ({ ...prev, transactionEntry: false }));
                            setSelectedTransaction(null);
                            setTransactionPrefilledData({});
                        }}
                        transactionTypeID={selectedTransactionType}
                        connID={connID}
                        companyName={connection?.BizName || ""}
                        prefilledData={transactionPrefilledData}
                        transactionID={selectedTransaction?.XnID || null}
                        onTransactionSaved={refreshConnectionData}
                    />
                )}

                {dialogStates.editDialog && (
                    <ConnectionEntryDialog
                        connID={connection?.ConnID}
                        triggerButton={false}
                        triggerText="Edit Contact"
                        editMode={contacts && contacts.length === 1 ? "ALL" : "CONNECTION_ONLY"}
                        open={dialogStates.editDialog}
                        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, editDialog: open, flag: open }))}
                        parentDialogSetter={onClose}
                        onConnectionSaved={refreshConnectionData}
                        scrollToEnd={dialogStates.flag}
                    />
                )}

                {dialogStates.editContactsDialog && (
                    <ConnectionEntryDialog
                        connID={connection?.ConnID}
                        triggerButton={false}
                        triggerText="Edit Contacts"
                        editMode="CONTACTS_ONLY"
                        open={dialogStates.editContactsDialog}
                        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, editContactsDialog: open }))}
                        parentDialogSetter={onClose}
                        onConnectionSaved={refreshConnectionData}
                    />
                )}

                {dialogStates.editSingleContactDialog && selectedContactForDetail && (
                    <ConnectionEntryDialog
                        connID={connection?.ConnID}
                        triggerButton={false}
                        triggerText="Edit Contact"
                        editMode="CONTACTS_ONLY"
                        selectedContactID={selectedContactForDetail?.CID}
                        open={dialogStates.editSingleContactDialog}
                        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, editSingleContactDialog: open }))}
                        parentDialogSetter={onClose}
                        onConnectionSaved={refreshConnectionData}
                    />
                )}

                {dialogStates.otherContacts && (
                    <OtherContactsEntry
                        connID={connection?.ConnID}
                        existingContacts={contacts.slice(1)}
                        isOpen={dialogStates.otherContacts}
                        onClose={() => setDialogStates((prev) => ({ ...prev, otherContacts: false }))}
                        bizName={connection?.BizName || "Contact"}
                        onContactSaved={refreshConnectionData}
                    />
                )}

                {dialogStates.recordHistory && (
                    <RecordHistoryDialog
                        isOpen={dialogStates.recordHistory}
                        onClose={() => setDialogStates((prev) => ({ ...prev, recordHistory: false, recordTypeFilter: null }))}
                        connID={connection?.ConnID}
                        records={records}
                        threadData={allThreads}
                        threadTypes={threadTypesData}
                        getExecutiveName={getExecutiveName}
                        getRelativeDateText={getRelativeDateText}
                        connectionName={connection?.BizName || connection?.ContactName}
                        onRefreshRecords={refreshConnectionData}
                        loading={loadingRecords}
                        recordTypeFilter={dialogStates.recordTypeFilter}
                        pendingRIDs={userPendingRIDs}
                        onRecordEntryOpen={() => {
                            setDialogStates((prev) => ({
                                ...prev,
                                recordHistory: false,
                                recordEntry: true
                            }))
                        }}
                    />
                )}

                {dialogStates.taskHistory && (
                    <TaskHistoryDialog
                        isOpen={dialogStates.taskHistory}
                        onClose={() => setDialogStates((prev) => ({ ...prev, taskHistory: false }))}
                        completedTasks={[]}
                    />
                )}

                {dialogStates.threadDetail && selectedThread && (
                    <ThrDetails
                        isOpen={dialogStates.threadDetail}
                        onClose={(open) => setDialogStates((prev) => ({ ...prev, threadDetail: open, isHistoryThread: false, isHistoryview: false }))}
                        threadID={selectedThread?.ThreadID}
                        isHistoryview={dialogStates.isHistoryThread || dialogStates.isHistoryview}
                        onThreadUpdated={handleThreadUpdated}
                        initialData={threadInitialData}
                    />
                )}

                {dialogStates.thrDetails && <ThrDetails
                    isOpen={dialogStates.thrDetails}
                    onClose={(open) => setDialogStates((prev) => ({ ...prev, thrDetails: open }))}
                    threadID={selectedThread?.ThreadID}
                    onThreadUpdated={handleThreadUpdated}
                />}

                {dialogStates.taskDetail && selectedTask && (
                    <TaskDetailDialog
                        isOpen={dialogStates.taskDetail}
                        onClose={() => {
                            setDialogStates(prev => ({ ...prev, taskDetail: false }));
                            setSelectedTask(null);
                        }}
                        taskID={selectedTask?.TskID}
                        openedFromConnectionDetails={true}
                    />
                )}

                {dialogStates.upcoming && (
                    <Upcoming
                        isOpen={dialogStates.upcoming}
                        onClose={() => setDialogStates((prev) => ({ ...prev, upcoming: false }))}
                        connID={connection?.ConnID}
                    />
                )}

                {/* Task Complete Alert */}
                <TaskCompleteDialog
                    open={dialogStates.confirmCompleteDialog}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCompleteDialog: open }))}
                    task={selectedTaskForRecord}
                    onAction={(actionValue, repeat) => {
                        if (actionValue === "complete") {
                            handleAction(selectedTaskForRecord, 'DONE', 'task');
                            if (repeat) openRepeatTaskEntry(selectedTaskForRecord);
                        } else if (actionValue === "record") {
                            pendingRepeatAfterRecordRef.current = repeat;
                            handleItemAction('RECORD_AND_COMPLETE', selectedTaskForRecord, 'task');
                        } else {
                            setDialogStates(prev => ({ ...prev, confirmCompleteDialog: false }));
                        }
                    }}
                />

                {/* Task Cancel/Reject Alert */}
                <TaskCompleteDialog
                    mode={(() => {
                        if (!selectedTaskForRecord) return "cancel";
                        const myUserIDs = getMyAgenciesMappedIDs('UserID');
                        const taskCreatorID = Number(selectedTaskForRecord.CreatedUID || selectedTaskForRecord.UID || 0);
                        const isMyCreator = myUserIDs.includes(taskCreatorID);
                        const isMyAssignee = myUserIDs.includes(Number(selectedTaskForRecord.UID)) || Number(selectedTaskForRecord.UID) === Number(currentUser?.UserID);
                        return (isMyAssignee && !isMyCreator) ? "reject" : "cancel";
                    })()}
                    open={dialogStates.confirmCancelDialog}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCancelDialog: open }))}
                    task={selectedTaskForRecord}
                    onAction={(actionValue) => {
                        if (actionValue === "yes") {
                            handleAction(selectedTaskForRecord, 'CANCEL', 'task');
                        } else {
                            setDialogStates(prev => ({ ...prev, confirmCancelDialog: false }));
                        }
                    }}
                />

                {/* Reassign Thread Dialog */}
                <ReassignDialog
                    open={dialogStates.reassignDialog}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, reassignDialog: open }))}
                    threadData={selectedThreadForAction}
                    onReassign={async (newPOCUserID) => {
                        try {
                            dispatch(setGlobalLoading(true));
                            const result = await reassignThreadAPI(selectedThreadForAction, newPOCUserID);
                            if (!result.error) {
                                toast.success(`${getThreadNameFromTypeID(selectedThreadForAction?.typeID, threadTypesData, "SnSng")} reassigned.`);
                                setDialogStates(prev => ({ ...prev, reassignDialog: false }));
                                setSelectedThreadForAction(null);
                            }
                        } catch (error) {
                            console.error('Error reassigning thread:', error);
                            toast.error(`Failed to reassign ${getThreadNameFromTypeID(selectedThreadForAction?.typeID, threadTypesData, "SnSng")}`);
                        } finally {
                            dispatch(setGlobalLoading(false));
                        }
                    }}
                />

                <ThreadEntry
                    triggerButton={false}
                    threadTypeID={2}
                    threadID={null}
                    open={dialogStates.convertToSales}
                    onOpenChange={async (open) => {
                        setDialogStates((prev) => ({ ...prev, convertToSales: open }));
                        if (!open) {
                            setSelectedThreadForAction(null);
                            // Refresh data when modal closes
                            await refreshConnectionData();
                        }
                    }}
                    preselectedConnID={connection?.ConnID || connection?.id}
                    preselectedContactPerson={connection?.ContactName || ""}
                    calledFrom="INQDONE"
                    prefilledData={selectedThreadForAction ? {
                        connID: connection?.ConnID,
                        contactPerson: connection?.ContactName,
                        product: selectedThreadForAction.mainProduct,
                        model: selectedThreadForAction.model,
                        reference: selectedThreadForAction.reference,
                        quantity: selectedThreadForAction.mainQty,
                        businessValue: selectedThreadForAction.bizProspect,
                        notes: selectedThreadForAction.notes,
                        source: selectedThreadForAction.source,
                        inquiryThreadID: selectedThreadForAction.threadID,
                        pocUserID: selectedThreadForAction.pocUserID,
                        sinceDT: selectedThreadForAction.sinceDT,
                    } : null}
                />

                {/* Sentiment Request Dialog  */}
                {/* <AlertBox
                    open={dialogStates.fdbkRequest}
                    onOpenChange={(open) =>
                        setDialogStates((prev) => ({ ...prev, fdbkRequest: open }))
                    }
                    title="Sentiment"
                    content={
                        <div>
                            {hasOwnEmail ? (
                                <p className="mb-1">Do you want to ask for sentiment?</p>
                            ) : (
                                <p className="mb-1">
                                    Please configure your email address from masters.
                                </p>
                            )}
                        </div>
                    }
                    actions={
                        hasOwnEmail
                            ? [
                                {
                                    label: "No",
                                    variant: "cancel",
                                    value: 0,
                                    className: "w-full sm:w-auto",
                                    icon: <X className="h-4 w-4" />,
                                },
                                {
                                    label: "Yes",
                                    variant: "action",
                                    value: 1,
                                    className: "w-full sm:w-auto",
                                    icon: <Check className="h-4 w-4" />,
                                },
                            ]
                            : [
                                {
                                    label: "Close",
                                    variant: "cancel",
                                    value: 0,
                                    className: "w-full sm:w-auto",
                                    icon: <X className="h-4 w-4" />,
                                },
                                {
                                    label: "Configure Email",
                                    variant: "action",
                                    value: 2,
                                    className: "w-full sm:w-auto",
                                    icon: <Settings className="h-4 w-4" />,
                                },
                            ]
                    }
                    onAction={(actionValue) => {
                        if (hasOwnEmail) {
                            if (actionValue === 1) {
                                handleFdbkRequest();
                            }
                        } else if (actionValue === 2) {
                            setDialogStates((prev) => ({ ...prev, emailConfig: true }));
                        }
                    }}
                /> */}
                <AlertBox
                    open={dialogStates.fdbkRequest}
                    onOpenChange={(open) =>
                        setDialogStates((prev) => ({ ...prev, fdbkRequest: open }))
                    }
                    title="Request Sentiment"
                    content={
                        <div>
                            <p className="mb-1">Would you like to ask {connection?.BizName || connection?.ContactName || 'Company'} how their experience is as your customer?</p>
                        </div>
                    }
                    actions={[
                        {
                            label: "No",
                            variant: "cancel",
                            value: 0,
                            className: "w-full sm:w-auto",
                            icon: <X className="h-4 w-4" />,
                        },
                        {
                            label: "Yes",
                            variant: "action",
                            value: 1,
                            className: "w-full sm:w-auto",
                            icon: <Check className="h-4 w-4" />,
                        },
                    ]
                    }
                    onAction={(actionValue) => {
                        if (actionValue === 1) {
                            handleFdbkRequest();
                        }
                    }}
                />

                {/* Email config dialog */}
                <Policies
                    open={dialogStates.emailConfig}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, emailConfig: open }))}
                    configType="EMAIL"
                />

                {/* Thread Complete Alert */}
                <CompleteThreadDialog
                    typeID={selectedThreadForAction?.TypeID}
                    open={dialogStates.confirmThreadCompleteDialog}
                    onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, confirmThreadCompleteDialog: open }))}
                    connID={connection?.ConnID}
                    onComplete={({ shareByEmail, shareByWhatsApp, takeFeedback }) => handleAction(selectedThreadForAction, 'DONE', 'thread', shareByEmail, takeFeedback, '', false, shareByWhatsApp)}
                    showFeedbackOption={true}
                />

                {/* Thread Cancel Alert */}
                <CancelThreadDialog
                    open={dialogStates.confirmThreadCancelDialog}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmThreadCancelDialog: open }))}
                    onConfirm={(rejReason, isMarkCold) => {
                        handleAction(selectedThreadForAction, 'CANCEL', 'thread', false, false, rejReason, isMarkCold);
                        setSelectedThreadForAction(null);
                    }}
                    rejectionReasonOptions={rejectionReasonOptions}
                    requireReason={true}
                    typeID={selectedThreadForAction?.TypeID}
                    currStageID={selectedThreadForAction?.CurrStageID || selectedThreadForAction?.currStageID}
                    disqlRsns={(() => {
                        if (!masterData?.disqlReasons || !selectedThreadForAction?.TypeID) return [];
                        try {
                            const currentThreadReasons = masterData.disqlReasons.find(item => item.TID === selectedThreadForAction.TypeID);
                            if (!currentThreadReasons || !currentThreadReasons.Rsns) return [];
                            return JSON.parse(currentThreadReasons.Rsns);
                        } catch (error) {
                            console.error("Error parsing disqualification reasons:", error);
                            return [];
                        }
                    })()}
                    connID={selectedThreadForAction?.ConnID}
                />

                {/* Hidden file input for attachment upload */}
                <input
                    type="file"
                    ref={attachFileInputRef}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
                    onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            const success = await handleUploadAttachment(file);
                            if (!success) {
                                setAttachDocDialog(true);
                            }
                        }
                        e.target.value = '';
                    }}
                />

                {/* Confirmation AlertDialog for Attaching Document */}
                <AlertDialog
                    open={attachDocDialog}
                    onOpenChange={(open) => {
                        if (!open) {
                            setAttachDocDialog(false);
                            setCompletedThreadForAttachment(null);
                        }
                    }}
                >
                    <AlertDialogContent className="w-[92%] sm:w-full max-w-md rounded-2xl p-4 bg-white gap-4 border border-zinc-200/80 shadow-2xl">
                        <AlertDialogHeader className="text-left gap-1.5">
                            <AlertDialogTitle className="text-base sm:text-lg font-semibold text-zinc-900">
                                Attach Document / Invoice
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-sm text-zinc-600 leading-normal">
                                Do you want to attach a document or invoice for this sale?
                                {/* <span className="font-semibold text-zinc-900">
                                    "{completedThreadForAttachment?.connectionData?.bizName || completedThreadForAttachment?.connectionData?.contactName || completedThreadForAttachment?.ContactPerson || completedThreadForAttachment?.MainProduct || (completedThreadForAttachment?.Reference ? `Sale #${completedThreadForAttachment.Reference}` : "this sale")}"
                                </span> */}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="!flex !flex-row sm:!justify-start gap-2.5 pt-1">
                            <Button
                                type="button"
                                onClick={() => {
                                    attachFileInputRef.current?.click();
                                }}
                                className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
                            >
                                <Check className="w-3.5 h-3.5 stroke-[2.5]" /> Yes, Attach
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setAttachDocDialog(false);
                                    setCompletedThreadForAttachment(null);
                                }}
                                className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
                            >
                                <X className="w-3.5 h-3.5" /> Cancel
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
                {/* Access Alerts & Dialogs */}
                <AlertBox
                    open={dialogStates.allUsersHaveAccessAlert}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, allUsersHaveAccessAlert: open }))}
                    title="Access Info"
                    content={
                        <div>
                            <p>All users have access to this connection already.</p>
                        </div>
                    }
                    actions={[
                        {
                            label: "OK",
                            variant: "action",
                            value: 0,
                            className: "w-full sm:w-auto",
                            icon: <Check className="h-4 w-4" />,
                        }
                    ]}
                    onAction={() => setDialogStates(prev => ({ ...prev, allUsersHaveAccessAlert: false }))}
                />

                <AlertBox
                    open={removeAccessState.open}
                    onOpenChange={(open) => setRemoveAccessState(prev => ({ ...prev, open }))}
                    title="Remove Access"
                    content={
                        <div>
                            <p>Are you sure you want to remove access of this connection from <strong>{users.find(u => u.userID === removeAccessState.userID)?.name}</strong>?</p>
                        </div>
                    }
                    actions={[
                        {
                            label: "No",
                            variant: "cancel",
                            value: 0,
                            icon: <X className="h-4 w-4" />,
                        },
                        {
                            label: "Yes",
                            variant: "action",
                            value: 1,
                            icon: <Check className="h-4 w-4" />,
                        },
                    ]}
                    onAction={async (val) => {
                        if (val === 1) {
                            try {
                                await manageConnectionAccessAPI(connID, 'ACCRMV', removeAccessState.userID);
                                setAccessUserIDs(prev => prev.filter(id => id !== removeAccessState.userID));
                                toast.success("Access removed.");
                            } catch (error) {
                                toast.error("Failed to remove access.");
                            }
                            setRemoveAccessState({ open: false, userID: null });
                        } else {
                            setRemoveAccessState(prev => ({ ...prev, open: false }));
                        }
                    }}
                />

                <ManageAccessDialog
                    open={dialogStates.accessDialog}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, accessDialog: open }))}
                    allUsers={users}
                    currentAccessIDs={[
                        ...users.filter(u => u.isAdmin || u.fullAccess || u.connViewTypeID === 1).map(u => u.userID),
                        ...accessUserIDs
                    ]}
                    setRemoveAccessState={setRemoveAccessState}
                    onSave={async (newID) => {
                        try {
                            await manageConnectionAccessAPI(connID, 'ACCADD', newID);
                            setAccessUserIDs(prev => [...prev, newID]);
                            toast.success("Access granted.");
                        } catch (error) {
                            toast.error("Failed to grant access.");
                        }
                    }}
                />

                {/* Contract Detail Dialog */}
                {dialogStates.contractDetail && (
                    <ContractDetailDialog
                        isOpen={dialogStates.contractDetail}
                        onClose={() => {
                            setDialogStates(prev => ({ ...prev, contractDetail: false }));
                            setSelectedContractForDetail(null);
                        }}
                        contract={selectedContractForDetail}
                        connection={connection}
                        currencySymbol={currencySymbol}
                        onContractUpdate={(updatedContract) => {
                            refreshConnectionData();
                        }}
                    />
                )}

                {dialogStates.contactDetail && selectedContactForDetail && (
                    <AlertBox
                        open={dialogStates.contactDetail}
                        onOpenChange={(open) => {
                            setDialogStates(prev => ({ ...prev, contactDetail: open }));
                            if (!open) setSelectedContactForDetail(null);
                        }}
                        title={
                            <>
                                {/* Header */}
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <h2 className="text-lg font-semibold truncate">
                                            {selectedContactForDetail?.CNm || "Unknown Contact"}
                                        </h2>
                                        {selectedContactForDetail?.isPrimary && (
                                            <Star
                                                className="h-4 w-4 flex-shrink-0"
                                                style={{ color: "var(--sahi-gold)" }}
                                                fill="var(--sahi-gold)"
                                            />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {!selectedContactForDetail?.isPrimary && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setDialogStates(prev => ({ ...prev, editSingleContactDialog: true, contactDetail: false }));
                                                }}
                                                className="h-8 w-8 p-0 hover:bg-sahi-blue-light"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setDialogStates(prev => ({ ...prev, contactDetail: false }));
                                                setSelectedContactForDetail(null);
                                            }}
                                            className="h-8 w-8 p-0 hover:bg-zinc-100"
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </>
                        }
                        content={
                            <div className="flex flex-col gap-3">
                                {/* Call Button */}
                                {selectedContactForDetail?.MNo && (
                                    <Button
                                        className="button-config text-base bg-sahi-dark-blue hover:text-sahi-dark-blue hover:border-sahi-dark-blue flex items-center py-[6px] px-[10px] w-full"
                                        onClick={async (e) => {
                                            e.stopPropagation(); // Prevent row click
                                            await handlePhoneClickAction(
                                                {
                                                    mobile: selectedContactForDetail.MNo,
                                                    cc: selectedContactForDetail.isPrimary ? connection?.CC : "91",
                                                    contName: selectedContactForDetail?.CNm || connection?.ContactName,
                                                },
                                                false,
                                                refreshConnectionData,
                                                connection?.ConnID
                                            );
                                        }}
                                    >
                                        <MdPhone className="h-5 w-5 " />
                                        <span className="text-base font-medium ">
                                            Call {selectedContactForDetail.MNo}
                                        </span>
                                    </Button>
                                )}

                                {/* WhatsApp Button */}
                                {selectedContactForDetail?.MNo && (
                                    <Button className="button-config text-base bg-sahi-green flex items-center py-[6px] px-[10px] w-full hover:text-sahi-green hover:border-sahi-green"
                                        onClick={async (e) => {
                                            e.stopPropagation(); // Prevent row click
                                            if (connection?.ConnType.includes('C')) {
                                                setIsWspDialogOpen(true);
                                            } else {
                                                await handleWhatsAppClickAction(
                                                    {
                                                        mobile: selectedContactForDetail.MNo,
                                                        cc: selectedContactForDetail.isPrimary ? connection?.CC : "91",
                                                        contName: selectedContactForDetail?.CNm || connection?.ContactName,
                                                    },
                                                    refreshConnectionData,
                                                    connection?.ConnID
                                                );
                                            }
                                        }}>
                                        <BsWhatsapp className="h-5 w-5 " />
                                        <span className="text-base font-medium ">
                                            Send WhatsApp
                                        </span>
                                    </Button>
                                )}

                                {/* Email Button */}
                                {selectedContactForDetail?.Eml && (
                                    <a
                                        href={createEmailLink(selectedContactForDetail.Eml)}
                                    >
                                        <Button className="button-config text-base bg-sahi-dark-blue flex items-center py-[6px] px-[10px] w-full hover:text-sahi-dark-blue hover:border-sahi-dark-blue">
                                            <BsEnvelopeFill className="h-5 w-5 " />
                                            <span className="text-base font-medium truncate ">
                                                {selectedContactForDetail.Eml}
                                            </span>
                                        </Button>
                                    </a>
                                )}
                            </div>
                        }
                    >
                    </AlertBox>
                )}
            </Dialog>

            {/* Full-view Modal Overlay using nested Dialog to bypass Radix focus/pointer traps */}
            <Dialog open={isFullPreviewOpen} onOpenChange={setIsFullPreviewOpen}>
                <DialogContent className="max-w-[100vw] h-screen md:max-h-[300px] max-h-screen bg-black/50 p-0 border-0 flex items-center justify-center z-[10000]">
                    {/* Close Button */}
                    <button
                        type="button"
                        className="absolute top-6 right-6 text-white hover:text-zinc-300 p-3 transition-colors focus:outline-none z-[10001] cursor-pointer flex items-center justify-center"
                        onClick={() => setIsFullPreviewOpen(false)}
                    >
                        <X className="h-6 w-6" />
                    </button>

                    {/* Spinner */}
                    {isFullPreviewLoading && (
                        <div className="absolute flex flex-col items-center gap-2 z-[10002]">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
                            <span className="text-xs text-white/70">Loading image...</span>
                        </div>
                    )}

                    <div
                        className="w-full h-full flex items-center justify-center p-4 cursor-pointer"
                        onClick={() => setIsFullPreviewOpen(false)}
                    >
                        <img
                            src={fullPreviewUrl}
                            alt="Full Preview"
                            className={`max-w-[95vw] md:max-h-[300px] max-h-[90vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${isFullPreviewLoading ? 'opacity-0 h-0 w-0' : 'opacity-100'
                                }`}
                            onLoad={() => setIsFullPreviewLoading(false)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </DialogContent>
            </Dialog>

            {/* Attachment Preview Modal for Sales/Orders & Other Documents */}
            <AttachmentPreviewModal
                open={Boolean(previewFilePath)}
                onOpenChange={(open) => {
                    if (!open) setPreviewFilePath(null);
                }}
                filePath={previewFilePath}
            />
        </TooltipProvider>
    )
}

const ManageAccessDialog = ({ open, onOpenChange, allUsers, currentAccessIDs, onSave, setRemoveAccessState }) => {
    const [selectedUserID, setSelectedUserID] = useState("");

    useEffect(() => {
        if (open) {
            setSelectedUserID("");
        }
    }, [open]);

    const handleSave = () => {
        if (!selectedUserID) return;
        onSave(Number(selectedUserID));
        onOpenChange(false);
    };

    const userOptions = allUsers
        .filter(u => !currentAccessIDs.includes(u.userID))
        .map(u => ({ label: u.name, value: String(u.userID) }));

    // Get executives who already have access
    const currentAccessUsers = allUsers.filter(u =>
        currentAccessIDs.includes(u.userID)
    );

    // Separate always visible and removable users
    const alwaysVisibleUsers = currentAccessUsers.filter(u =>
        u.isAdmin || u.fullAccess || u.connViewTypeID === 1
    );

    const removableUsers = currentAccessUsers.filter(u =>
        u.connViewTypeID === 4 && !u.isAdmin && !u.fullAccess
    );

    const renderHeader = () => (
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
            <div className="flex items-center justify-between w-full h-8">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                        <ArrowLeft className="h-6 w-6" />
                        <span className="sr-only">Back</span>
                    </DialogClose>
                    <DialogTitle className="text-lg font-medium">
                        Manage Access
                    </DialogTitle>
                </div>
            </div>
        </DialogHeader>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex flex-col w-full h-full max-w-full max-h-full p-0 sm:max-w-[400px] sm:h-auto sm:rounded-lg">
                {renderHeader()}

                <div className="px-4 flex-1 overflow-y-auto space-y-4">
                    {/* Current Access Section */}
                    {currentAccessUsers.length > 0 && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Current Access</label>
                            <div className="flex flex-wrap gap-2 bg-white rounded-lg p-3">
                                {alwaysVisibleUsers.map(user => (
                                    <span
                                        key={`static-${user.userID}`}
                                        className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold border-none transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-zinc-100 hover:bg-zinc-200 h-7 sahi-badge"
                                    >
                                        {user.name}
                                    </span>
                                ))}

                                {removableUsers.map(user => (
                                    <span
                                        key={`removable-${user.userID}`}
                                        className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold border-none transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-zinc-100 hover:bg-zinc-200 h-7 sahi-badge"
                                    >
                                        {user.name}
                                        <X
                                            className="ml-2 h-3 w-3 cursor-pointer text-zinc-500 hover:text-sahi-red"
                                            onClick={() => setRemoveAccessState({ open: true, userID: user.userID })}
                                        />
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Add New Access Section */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Executive</label>
                        <CustomSearchableDropdown
                            options={userOptions}
                            value={selectedUserID}
                            onSelect={setSelectedUserID}
                            placeholder="Select Executive"
                            searchPlaceholder="Search executives..."
                            displayKey="label"
                            valueKey="value"
                            allowAdd={false}
                        />
                    </div>

                    <div className="mt-4 flex flex-col gap-3 w-full">
                        <Button
                            onClick={handleSave}
                            disabled={!selectedUserID}
                        >
                            <Check className="h-4 w-4 mr-2" />
                            Add
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ConnectionDetailsMobile;
